/* ═══════════════════════════════════════════════════════════════
   SERMENT — theme.js
   Native Shopify cart + AJAX drawer + conversion pack.
   Vanilla JS only — no external scripts.
   ═══════════════════════════════════════════════════════════════ */

const SERMENT_CURRENCY = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
const SERMENT_FREE_SHIP = parseFloat(document.body.dataset.freeShip || '150');

const SERMENT_LANG = (document.documentElement.lang || 'en');
/* Translated strings passed by layout/theme.liquid via <body data-t-*> */
const sermentT = (k, f) => (document.body.dataset[k] || f);
/* Locale root ('/' or '/fr/') so links stay in the visitor's language */
const sermentRoot = () => { const r = document.body.dataset.root || (window.Shopify && Shopify.routes && Shopify.routes.root) || '/'; return r.slice(-1) === '/' ? r : r + '/'; };
const sermentFmt = cents => new Intl.NumberFormat(SERMENT_LANG, { style: 'currency', currency: SERMENT_CURRENCY }).format(cents / 100);
const sermentPad = n => String(n).padStart(2, '0');
const sermentEsc = s => String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/* Line item properties (e.g. custom size measurements) — shown under the title */
const sermentProps = it => {
  const p = it.properties || {};
  return Object.keys(p)
    .filter(k => p[k] && typeof p[k] === 'string' && k.charAt(0) !== '_')
    .map(k => `<div class="cs-prop">${sermentEsc(k)}: ${sermentEsc(p[k])}</div>`)
    .join('');
};

/* ═══ CART DRAWER (AJAX) ═══ */
/* ═══ GIFT HELPER — random draws from collections (products.json) ═══ */
const SermentGift = {
  shuffle(a){ for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; },
  /* n random available products drawn from up to 2 random collections, random page (maxPage) */
  async fetchPool(handles, n, maxPage){
    /* every collection is queried (small catalogues online), a random page each; products with a photo first */
    const lists = await Promise.all(handles.map(async h => {
      const page = 1 + Math.floor(Math.random() * (maxPage || 2));
      const get = pg => fetch(`${sermentRoot()}collections/${h}/products.json?limit=50&page=${pg}`).then(r => r.ok ? r.json() : { products: [] }).then(d => d.products || []).catch(() => []);
      let ps = await get(page); if (!ps.length && page > 1) ps = await get(1); return ps;
    }));
    const seen = {};
    const all = [].concat(...lists).filter(p => p.variants && p.variants.some(v => v.available) && !seen[p.handle] && (seen[p.handle] = 1));
    const withImg = this.shuffle(all.filter(p => p.images && p.images.length));
    const noImg = this.shuffle(all.filter(p => !(p.images && p.images.length)));
    return withImg.concat(noImg).slice(0, n);
  },
  img(p, w){ const i = p.images && p.images[0]; const src = typeof i === 'string' ? i : (i && i.src); return src ? src.replace(/(\.[a-z]+)(\?.*)?$/i, `_${w}x$1`) : ''; },
  variant(p){ return p.variants.find(x => x.available) || p.variants[0]; },
  /* Line properties for an accessory being added: timestamp + `_gift=1` if no accessory is the gift yet */
  async accessoryProps(explicitGift){
    let hasGift = false;
    try { const c = await fetch('/cart.js').then(r => r.json()); hasGift = c.items.some(i => i.properties && i.properties._gift === '1'); } catch (e) {}
    const props = { _gift_acc: '1', _gift_at: String(Date.now()) };
    if (explicitGift || !hasGift) props._gift = '1';
    return props;
  },
  /* Keeps exactly one `_gift` line: the FIRST accessory added (smallest _gift_at). Returns true when the cart was changed. */
  async normalize(cart){
    const accs = cart.items.filter(i => i.properties && (i.properties._gift_acc || i.properties._gift));
    if (!accs.length) return false;
    const at = i => parseInt((i.properties && i.properties._gift_at) || '0', 10) || Number.MAX_SAFE_INTEGER;
    const first = accs.slice().sort((a, b) => at(a) - at(b))[0];
    let changed = false;
    for (const it of accs) {
      const props = Object.assign({}, it.properties || {});
      const want = it.key === first.key;
      if (want && props._gift === '1') continue;
      if (!want && !props._gift) continue;
      if (want) props._gift = '1'; else delete props._gift;
      await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: it.key, quantity: it.quantity, properties: props }) }).catch(() => {});
      changed = true;
    }
    return changed;
  },
  cents(p){ const v = this.variant(p); return typeof v.price === 'string' ? Math.round(parseFloat(v.price) * 100) : v.price; }
};

const SermentDrawer = {
  timerInterval: null,

  ensure(){
    if (document.getElementById('miniCart')) return;
    const wrap = document.createElement('div');
    wrap.innerHTML = `
      <div class="drawer-overlay" id="drawerOverlay"></div>
      <aside class="drawer" id="miniCart" aria-label="Cart">
        <div class="drawer-head">
          <h3>${sermentT('tCartTitle', 'Your cart')} <span id="drawerCount"></span></h3>
          <button class="drawer-close" id="drawerClose" type="button">${sermentT('tClose', 'Close')} ✕</button>
        </div>
        <div id="drawerTimerZone"></div>
        <div class="drawer-body" id="drawerBody"></div>
        <div id="drawerGift"></div>
        <div id="drawerUpsellZone"></div>
        <div class="drawer-foot" id="drawerFoot"></div>
      </aside>`;
    document.body.appendChild(wrap);
    document.getElementById('drawerOverlay').addEventListener('click', () => this.close());
    document.getElementById('drawerClose').addEventListener('click', () => this.close());
    document.addEventListener('keydown', e => { if (e.key === 'Escape') this.close() });
  },
  open(){ this.ensure(); this.render(); document.body.classList.add('drawer-open') },
  close(){ document.body.classList.remove('drawer-open') },

  badge(cart){
    document.querySelectorAll('.cart-count').forEach(b => {
      b.textContent = cart.item_count;
      b.classList.toggle('on', cart.item_count > 0);
    });
  },

  /* Reservation timer (visual) */
  startTimer(){
    if (document.body.dataset.cartTimer !== 'true') return;
    const zone = document.getElementById('drawerTimerZone');
    if (!zone) return;
    const minutes = parseInt(document.body.dataset.cartTimerMin || '10', 10);
    const label = document.body.dataset.cartTimerText || 'Your cart is reserved for';
    let end = parseInt(sessionStorage.getItem('serment_cart_timer') || '0', 10);
    if (!end || end < Date.now()) {
      end = Date.now() + minutes * 60000;
      sessionStorage.setItem('serment_cart_timer', String(end));
    }
    zone.innerHTML = `
      <div class="drawer-timer">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="13" r="8"/><path d="M12 9v4l2.5 2.5M9 2h6"/></svg>
        <span>${label} <b id="drawerTimerVal">--:--</b></span>
      </div>`;
    const el = document.getElementById('drawerTimerVal');
    clearInterval(this.timerInterval);
    const tick = () => {
      let left = Math.max(0, end - Date.now());
      if (left <= 0) {
        end = Date.now() + minutes * 60000;
        sessionStorage.setItem('serment_cart_timer', String(end));
        left = minutes * 60000;
      }
      const m = Math.floor(left / 60000), s = Math.floor((left % 60000) / 1000);
      el.textContent = `${sermentPad(m)}:${sermentPad(s)}`;
    };
    tick();
    this.timerInterval = setInterval(tick, 1000);
  },

  /* One-click upsell */
  renderUpsell(cart){
    const zone = document.getElementById('drawerUpsellZone');
    if (!zone) return;
    const dataEl = document.getElementById('sermentUpsellData');
    if (!dataEl || !cart.item_count) { zone.innerHTML = ''; return; }
    let data;
    try { data = JSON.parse(dataEl.textContent) } catch(e) { return }
    const already = cart.items.some(it => it.variant_id === data.variantId);
    if (already) { zone.innerHTML = ''; return; }
    zone.innerHTML = `
      <div class="drawer-upsell">
        <h4>${data.heading}</h4>
        <div class="up-card">
          <a class="u-thumb" href="${data.url}">${data.image ? `<img src="${data.image}" alt="" loading="lazy">` : ''}</a>
          <div>
            <div class="u-name">${data.title}</div>
            <div class="u-price">${sermentFmt(data.price)}</div>
          </div>
          <button type="button" aria-label="Add ${data.title} to cart" data-upsell-add="${data.variantId}">+</button>
        </div>
      </div>`;
  },


  /* — Cart drawer: paid suggestions (accessories with a gown, gowns with an accessory) — */
  renderGift(cart){
    const zone = document.getElementById('drawerGift');
    if (!zone) return;
    if (!cart.items.length) { zone.innerHTML = ''; return; }
    const aRobe = cart.items.some(i => /wedding dress|robe de mari/i.test(i.product_type || ''));
    const pools = aRobe
      ? ['bridal-accessories']
      : ['wedding-dress'];
    const titre = aRobe ? sermentT('tSuggGownTitle', 'Complete your gown') : sermentT('tSuggAccTitle', 'Wear it with');
    const texte = aRobe ? sermentT('tSuggGownText', 'Chosen to sit beautifully with it.') : sermentT('tSuggAccText', 'The gowns this piece was chosen for.');
    const tout  = aRobe ? sermentT('tSuggAllAcc', 'See all accessories') : sermentT('tSuggAllGowns', 'See all gowns');
    const lien  = sermentRoot() + (aRobe ? 'collections/bridal-accessories' : 'collections/wedding-dress');
    const deja  = cart.items.map(i => i.handle);
    SermentGift.fetchPool(pools, 6, 2).then(ps => {
      const liste = ps.filter(p => deja.indexOf(p.handle) === -1).slice(0, 3);
      if (!liste.length) { zone.innerHTML = ''; return; }
      zone.innerHTML = `<div class="d-gift">
        <div class="d-gift-t">${titre}</div>
        <p class="d-gift-p">${texte}</p>
        <div class="d-gift-grid">${liste.map(p => { const v = SermentGift.variant(p); const img = SermentGift.img(p, 200); return `
          <div class="d-gift-card">
            <a class="c-thumb t-stone" href="${sermentRoot()}products/${p.handle}">${img ? `<img src="${img}" alt="" class="c-photo" loading="lazy">` : ''}</a>
            <div class="d-gift-name">${sermentEsc(p.title)}</div>
            <div class="d-gift-prix">${sermentFmt(SermentGift.cents(p))}</div>
            ${aRobe ? `<button type="button" class="d-gift-add" data-upsell-add="${v.id}">${sermentT('tAdd', 'Add to cart')}</button>` : `<a class="d-gift-add d-gift-go" href="${sermentRoot()}products/${p.handle}">${sermentT('tChooseSize', 'Choose size')}</a>`}
          </div>`; }).join('')}</div>
        <a class="lien" href="${lien}">${tout}</a>
      </div>`;
    }).catch(() => { zone.innerHTML = ''; });
  },
  /* — Customer's choice of the offered accessory (needs the gift-choice Function): sets _gift=1 on one line — */
  async pickGift(key){
    const cart = await fetch('/cart.js').then(r => r.json());
    for (const it of cart.items) {
      const props = Object.assign({}, it.properties || {});
      const isAcc = props._gift_acc || props._gift;
      if (!isAcc) continue;
      const want = it.key === key;
      if (want && props._gift === '1') continue;
      if (!want && !props._gift) continue;
      if (want) props._gift = '1'; else delete props._gift;
      await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: it.key, quantity: it.quantity, properties: props }) }).catch(() => {});
    }
    this.render();
  },
  addGift(id){
    return SermentGift.accessoryProps(false)
      .then(props => fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: id, quantity: 1, properties: props }] }) }))
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(() => this.render());
  },
  render(){
    this.ensure();
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        this.badge(cart);
        const body = document.getElementById('drawerBody');
        const foot = document.getElementById('drawerFoot');
        document.getElementById('drawerCount').textContent = cart.item_count ? `(${cart.item_count})` : '';

        if (!cart.item_count){
          document.getElementById('drawerTimerZone').innerHTML = '';
          clearInterval(this.timerInterval);
          body.innerHTML = `<div class="drawer-empty">
            <p style="font-family:var(--serif);font-weight:800;font-size:18px;text-transform:uppercase;margin-bottom:8px">${sermentT('tEmptyTitle', 'Your cart is empty')}</p>
            <p style="font-size:13px">${sermentT('tEmptyText', 'The most beautiful part is still to choose.')}</p>
          </div>`;
          this.renderUpsell(cart);
          const gz = document.getElementById('drawerGift'); if (gz) gz.innerHTML = '';
          foot.innerHTML = `<a class="btn full" href="${sermentRoot()}collections/all">${sermentT('tEmptyCta', 'Explore the gowns')}</a>`;
          return;
        }

        this.startTimer();
        if (!this._normalized) {
          this._normalized = true;
          SermentGift.normalize(cart).then(changed => { this._normalized = false; if (changed) this.render(); });
        }
        this.renderGift(cart);

        const giftChoice = false;
        body.innerHTML = cart.items.map(it => `
          <div class="d-line">
            <div class="c-thumb t-stone">${it.image
              ? `<img src="${it.image.replace(/(\.[a-z]+)(\?.*)?$/i, '_200x$1')}" alt="" class="c-photo" loading="lazy">`
              : ''}</div>
            <div>
              <div class="name">${it.product_title}</div>
              ${(() => { const vt = (it.variant_title || '').split(' / ').filter(s => !/^(china mainland|cn)$/i.test(s.trim())).join(' / '); return vt && vt !== 'Default Title' ? `<div class="variant">${vt}</div>` : ''; })()}
              ${sermentProps(it)}
              ${giftChoice && it.properties && (it.properties._gift_acc || it.properties._gift) ? `<label class="d-gift-pick"><input type="radio" name="sermentGiftPick" ${(it.final_line_price === 0 && it.original_line_price > 0) ? 'checked' : ''} onchange="SermentDrawer.pickGift('${it.key}')"> ${document.body.dataset.giftPick || 'Offered with my gown'}</label>` : ''}
              <div class="qty">
                <button type="button" aria-label="${sermentT('tQtyDec', 'Decrease quantity')}" onclick="SermentDrawer.change('${it.key}', ${it.quantity - 1})">−</button>
                <input value="${it.quantity}" readonly aria-label="${sermentT('tQuantity', 'Quantity')}">
                <button type="button" aria-label="${sermentT('tQtyInc', 'Increase quantity')}" onclick="SermentDrawer.change('${it.key}', ${it.quantity + 1})">+</button>
              </div>
            </div>
            <div class="line-right">
              <div class="line-price">${(it.final_line_price === 0 && it.original_line_price > 0) ? '<span class="line-offered">' + (document.body.dataset.giftOffered || 'Offered') + '</span>' : sermentFmt(it.final_line_price)}</div>
              <button class="c-remove" type="button" onclick="SermentDrawer.change('${it.key}', 0)">${sermentT('tRemove', 'Remove')}</button>
            </div>
          </div>`).join('');

        this.renderUpsell(cart);


        foot.innerHTML = `
          <div class="sum-row total" style="border:none;padding-top:0;margin-top:0"><span>${sermentT('tSubtotal', 'Subtotal')}</span><span>${sermentFmt(cart.total_price)}</span></div>
          <div class="actions">
            <a class="btn red full" href="${sermentRoot()}checkout">${sermentT("tCheckout", 'Checkout')}</a>
            <button class="btn ghost full no-arrow" type="button" onclick="SermentDrawer.close()">${sermentT('tContinue', 'Continue shopping')}</button>
          </div>
          <div class="c-reassure">
            <span><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M2.5 8.5l3.5 3.5 7-8"/></svg> ${sermentT('tReassure1', 'Secure card payment &mdash; encrypted checkout')}</span>
            <span><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M2.5 8.5l3.5 3.5 7-8"/></svg> ${sermentT('tReassure2', 'Online order tracking at every stage')}</span>
            <span><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M2.5 8.5l3.5 3.5 7-8"/></svg> ${sermentT('tReassure3', 'Customer care 7 days a week, reply within 24 hours')}</span>
          </div>`;
      })
      .catch(() => {});
  },

  change(key, quantity){
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: Math.max(0, quantity) })
    })
      .then(r => r.json())
      .then(() => this.render())
      .catch(() => {});
  },

  add(variantId, qty){
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: qty || 1 }] })
    }).then(r => { if (!r.ok) throw new Error(); return r.json(); });
  }
};

/* ═══ GENERIC COUNTDOWNS ═══
   [data-countdown] with data-mode="midnight"|"duration", data-hours, data-key.
   The "duration" mode is stored in localStorage and resets when it expires. */
function sermentCountdowns(){
  document.querySelectorAll('[data-countdown]').forEach(el => {
    const mode = el.dataset.mode || 'midnight';
    const hours = parseFloat(el.dataset.hours || '4');
    const key = 'serment_cd_' + (el.dataset.key || 'default');
    const showDays = el.dataset.days === 'true';

    const getEnd = () => {
      if (mode === 'midnight') {
        const d = new Date();
        d.setHours(24, 0, 0, 0);
        return d.getTime();
      }
      let end = parseInt(localStorage.getItem(key) || '0', 10);
      if (!end || end < Date.now()) {
        end = Date.now() + hours * 3600000;
        localStorage.setItem(key, String(end));
      }
      return end;
    };

    let end = getEnd();
    const tick = () => {
      let left = end - Date.now();
      if (left <= 0) { end = getEnd(); left = Math.max(0, end - Date.now()); }
      const d = Math.floor(left / 86400000);
      const h = Math.floor((left % 86400000) / 3600000);
      const m = Math.floor((left % 3600000) / 60000);
      const s = Math.floor((left % 60000) / 1000);
      el.textContent = (showDays && d > 0 ? `${d}d ` : '') +
        `${sermentPad(showDays ? h : h + d * 24)}:${sermentPad(m)}:${sermentPad(s)}`;
    };
    tick();
    setInterval(tick, 1000);
  });
}

/* ═══ DYNAMIC DELIVERY DATES ═══ */
function sermentDelivery(){
  document.querySelectorAll('[data-delivery]').forEach(el => {
    const j1 = parseInt(el.dataset.j1 || '18', 10);
    const j2 = parseInt(el.dataset.j2 || '30', 10);
    const fmt = new Intl.DateTimeFormat(SERMENT_LANG, { month: 'long', day: 'numeric' });
    const d1 = new Date(); d1.setDate(d1.getDate() + j1);
    const d2 = new Date(); d2.setDate(d2.getDate() + j2);
    const s1 = el.querySelector('[data-date-1]');
    const s2 = el.querySelector('[data-date-2]');
    if (s1) s1.textContent = fmt.format(d1);
    if (s2) s2.textContent = fmt.format(d2);
  });
}

/* ═══ CUSTOM SIZE (line item properties) ═══
   Marketplace-style modal inside the product form: cm/inches unit
   select, 5 measurement selects, cancel with restore, summary line
   after saving, validation before add to cart. */
function sermentCustomSize(){
  const wrap = document.querySelector('[data-custom-size]');
  if (!wrap || wrap.dataset.csReady === '1') return;
  wrap.dataset.csReady = '1';
  const form = document.getElementById('product-form');
  const selects = Array.from(wrap.querySelectorAll('[data-cs-select]'));
  const trigger = wrap.querySelector('[data-cs-toggle]');
  const overlay = wrap.querySelector('[data-csm-overlay]');
  const box = overlay ? overlay.querySelector('.csm-box') : null;
  const unitSel = wrap.querySelector('[data-cs-unit-select]');
  const errEl = wrap.querySelector('[data-csm-error]');
  const sumWrap = wrap.querySelector('[data-cs-summary]');
  const sumText = wrap.querySelector('[data-cs-summary-text]');
  const specsEl = wrap.querySelector('[data-cs-specs]');
  const sufCm = wrap.dataset.cmSuffix || 'cm';
  const sufIn = wrap.dataset.inSuffix || 'in';
  const defUnit = wrap.dataset.defaultUnit || 'cm';
  const kw = (wrap.dataset.keyword || '').toLowerCase();
  const vSelect = document.querySelector('[data-variant-select]');
  const pillsSrc = vSelect || document.querySelector('[data-variant-pills]');
  let saved = false;
  selects.forEach(sel => { sel.dataset.unit = defUnit; });

  const unitOf = () => {
    const o = unitSel ? unitSel.options[unitSel.selectedIndex] : null;
    return (o && o.dataset.unit) || defUnit;
  };

  /* — cm / inch switch: rebuild options (1 cm / 0.5 in steps), keep nearest value — */
  const suffix = unit => unit === 'in' ? sufIn : sufCm;
  const build = unit => {
    selects.forEach(sel => {
      const cmMin = parseFloat(sel.dataset.cmMin), cmMax = parseFloat(sel.dataset.cmMax);
      const prev = parseFloat(sel.value);
      const prevUnit = sel.dataset.unit || defUnit;
      let want = NaN;
      if (!isNaN(prev)) want = prevUnit === unit ? prev : (unit === 'in' ? prev / 2.54 : prev * 2.54);
      const placeholder = sel.options[0];
      sel.innerHTML = '';
      sel.appendChild(placeholder);
      const step = unit === 'in' ? 0.5 : 1;
      const min = unit === 'in' ? Math.ceil(cmMin / 2.54 * 2 - 1e-9) / 2 : cmMin;
      const max = unit === 'in' ? Math.round(cmMax / 2.54 * 2) / 2 : cmMax;
      let best = null;
      for (let v = min; v <= max + 1e-9; v += step) {
        const val = Math.round(v * 2) / 2;
        const o = document.createElement('option');
        o.value = `${val} ${suffix(unit)}`;
        o.textContent = o.value;
        sel.appendChild(o);
        if (!isNaN(want) && (best === null || Math.abs(val - want) < Math.abs(best - want))) best = val;
      }
      sel.value = best === null ? '' : `${best} ${suffix(unit)}`;
      sel.dataset.unit = unit;
    });
  };
  if (unitSel) unitSel.addEventListener('change', () => build(unitOf()));

  /* — Per-field messages (marketplace style) — */
  const msgOf = sel => sel.parentElement.querySelector('[data-cs-msg]');
  const validate = show => {
    let ok = true;
    selects.forEach(sel => {
      const bad = !sel.value;
      if (show) {
        sel.classList.toggle('err', bad);
        const m = msgOf(sel);
        if (m) m.hidden = !bad;
      }
      if (bad) ok = false;
    });
    return ok;
  };
  const clearErrors = () => selects.forEach(sel => {
    sel.classList.remove('err');
    const m = msgOf(sel);
    if (m) m.hidden = true;
  });
  selects.forEach(sel => sel.addEventListener('change', () => {
    if (!sel.value) return;
    sel.classList.remove('err');
    const m = msgOf(sel);
    if (m) m.hidden = true;
  }));

  /* — Modal: open, cancel (restore snapshot), save — */
  let snap = null;
  const takeSnap = () => ({
    unit: unitSel ? unitSel.value : '',
    unitCode: unitOf(),
    values: selects.map(s => s.value)
  });
  const restoreSnap = () => {
    if (!snap) return;
    if (unitSel && unitSel.value !== snap.unit) {
      unitSel.value = snap.unit;
      build(snap.unitCode);
    }
    selects.forEach((s, i) => { s.value = snap.values[i]; });
    clearErrors();
    if (errEl) errEl.hidden = true;
  };
  const openModal = () => {
    if (wrap.classList.contains('csm-open')) return;
    snap = takeSnap();
    wrap.classList.add('csm-open');
    document.body.classList.add('csm-lock');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    if (box) box.scrollTop = 0;
    const first = unitSel || selects[0];
    if (first && !first.disabled) first.focus();
  };
  const closeModal = () => {
    wrap.classList.remove('csm-open');
    document.body.classList.remove('csm-lock');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  };
  const cancelModal = () => { restoreSnap(); closeModal(); };

  const renderSummary = () => {
    if (!sumWrap || !sumText) return;
    if (!selects.every(s => s.value)) { sumWrap.hidden = true; return; }
    const colon = wrap.dataset.colon || ': ';
    sumText.textContent = selects.map(sel => (sel.dataset.csLabel || '') + colon + sel.value).join(' · ');
    sumWrap.hidden = false;
  };
  const saveModal = () => {
    if (!validate(true)) return;
    saved = true;
    if (errEl) errEl.hidden = true;
    renderSummary();
    closeModal();
  };

  if (trigger) trigger.addEventListener('click', openModal);
  wrap.querySelectorAll('[data-cs-edit]').forEach(b => b.addEventListener('click', openModal));
  wrap.querySelectorAll('[data-csm-cancel]').forEach(b => b.addEventListener('click', cancelModal));
  wrap.querySelectorAll('[data-csm-save]').forEach(b => b.addEventListener('click', saveModal));
  if (overlay) overlay.addEventListener('click', e => { if (e.target === overlay) cancelModal(); });
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && wrap.classList.contains('csm-open') && document.body.contains(wrap)) cancelModal();
  });

  /* — Standard size line: "Bust: 130 cm / 51 in · …" for the selected size — */
  let specs = {};
  try { specs = JSON.parse(wrap.dataset.sizeSpecs || '{}'); } catch (e) { specs = {}; }
  const norm = v => String(v || '').toLowerCase().replace(/^.*\|/, '').replace(/^us\s*/, '').replace(/\s+/g, '');
  const specLabels = (wrap.dataset.specsLabels || 'Bust|Waist|Hips|Hollow to hem').split('|');
  const optGroups = () => Array.from(document.querySelectorAll('[data-opt-group]'));
  const sizeGroup = () => {
    const gs = optGroups();
    const inputs = g => Array.from(g.querySelectorAll('input'));
    return gs.find(g => inputs(g).some(i => Array.isArray(specs[norm(i.value)])))
        || gs.find(g => /size|taille/i.test(g.previousElementSibling ? g.previousElementSibling.textContent : ''))
        || gs.find(g => inputs(g).some(i => kw && i.value.toLowerCase().indexOf(kw) !== -1)) || null;
  };
  const renderSpecs = () => {
    if (!specsEl) return;
    const g = sizeGroup();
    const c = g ? g.querySelector('input:checked') : null;
    const row = c ? specs[norm(c.value)] : null;
    if (!Array.isArray(row)) { specsEl.hidden = true; return; }
    const colon = wrap.dataset.colon || ': ';
    specsEl.innerHTML = row.map((v, i) => {
      const cm = parseFloat(v);
      if (isNaN(cm)) return '';
      const inch = Math.round(cm / 2.54 * 2) / 2;
      return '<span><b>' + (specLabels[i] || '') + colon + '</b>' + cm + ' cm / ' + inch + ' in</span>';
    }).join('');
    specsEl.hidden = false;
  };

  /* — Variant mode: only the SIZE option decides. "Custom size" opens the modal,
       a colour change or a "Custom color" value never does — */
  if (wrap.dataset.mode === 'variant') {
    const isSizeName = n => /size|taille|gr[oö]ße|talla/i.test(n || '');
    const groupName = g => (g.previousElementSibling ? g.previousElementSibling.textContent : '') || '';
    const hasKw = g => Array.from(g.querySelectorAll('input')).some(i => kw && i.value.toLowerCase().indexOf(kw) !== -1);
    const findSizeGroup = () => {
      const withKw = optGroups().filter(hasKw);
      return withKw.find(g => isSizeName(groupName(g)))
          || withKw.find(g => Array.from(g.querySelectorAll('input')).some(i => Array.isArray(specs[norm(i.value)])))
          || withKw[0] || null;
    };
    const curTitle = () => {
      if (vSelect) { const o = vSelect.options[vSelect.selectedIndex]; return o ? o.textContent.toLowerCase() : ''; }
      const pills = document.querySelector('[data-variant-pills]');
      const checked = pills ? pills.querySelector('input[name="id"]:checked') : null;
      const pill = checked ? checked.closest('.v-pill') : null;
      return pill ? pill.textContent.toLowerCase() : '';
    };
    const isCustom = () => {
      const g = findSizeGroup();
      if (g) { const c = g.querySelector('input:checked'); return !!c && c.value.toLowerCase().indexOf(kw) !== -1; }
      return kw !== '' && curTitle().indexOf(kw) !== -1;
    };
    let prevCustom = null;
    const apply = userChange => {
      const show = isCustom();
      wrap.classList.toggle('cs-off', !show);
      if (form) form.classList.toggle('is-custom', show); /* hides the express checkout buttons (.pf-dyn) while a custom size is selected */
      wrap.querySelectorAll('select, input').forEach(el => { el.disabled = !show; });
      if (!show && wrap.classList.contains('csm-open')) cancelModal();
      if (show && userChange && prevCustom === false && !saved) openModal();
      prevCustom = show;
      renderSpecs();
    };
    if (pillsSrc) pillsSrc.addEventListener('change', () => apply(true));
    document.querySelectorAll('[data-opt-group] .v-pill').forEach(p => p.addEventListener('click', () => {
      const inp = p.querySelector('input');
      const g = findSizeGroup();
      if (!inp || !kw || !g || !g.contains(inp) || inp.value.toLowerCase().indexOf(kw) === -1) return;
      setTimeout(() => { if (!wrap.classList.contains('cs-off')) openModal(); }, 60);
    }));
    apply(false);
  } else {
    if (form) form.classList.add('is-custom'); /* panel always active in 'always' / 'tag' modes: express checkout hidden too */
    renderSpecs();
    if (pillsSrc) pillsSrc.addEventListener('change', renderSpecs);
  }

  /* — Add to cart (submit + sticky ATC): required measurements
       missing -> open the modal with the field messages — */
  if (form) {
    document.addEventListener('submit', e => {
      if (e.target !== form || !document.body.contains(wrap)) return;
      if (wrap.dataset.required !== 'true' || wrap.classList.contains('cs-off')) return;
      if (validate(true)) { if (errEl) errEl.hidden = true; return; }
      e.preventDefault();
      e.stopPropagation();
      (form.dataset.buyNow="",openModal());
      validate(true);
    }, true);
  }
}
document.addEventListener('DOMContentLoaded', () => {

  SermentDrawer.ensure();
  fetch('/cart.js').then(r => r.json()).then(c => SermentDrawer.badge(c)).catch(() => {});

  /* — Header shadow on scroll — */
  const header = document.querySelector('.site-header');
  if (header) {
    const onScroll = () => header.classList.toggle('scrolled', window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* — Mobile menu — */
  const burger = document.getElementById('burgerBtn');
  if (burger) burger.addEventListener('click', () => document.body.classList.add('nav-open'));
  document.querySelectorAll('.nav-close, .main-nav a').forEach(el =>
    el.addEventListener('click', () => document.body.classList.remove('nav-open')));

  /* — Reveal animations (global setting) — */
  if (document.body.dataset.animations === 'true') {
    document.querySelectorAll('#main .shopify-section').forEach(s => {
      /* The pinned hero stays out of the reveal flow (sticky + transform don't mix) */
      if (s.querySelector('.couture-hero')) return;
      s.classList.add('reveal');
    });
  }
  const io = new IntersectionObserver(entries => {
    entries.forEach(en => { if (en.isIntersecting) { en.target.classList.add('in'); io.unobserve(en.target); } });
  }, { threshold: .08 });
  document.querySelectorAll('.reveal').forEach(el => io.observe(el));

  /* — Quick add (product cards) — */
  document.addEventListener('click', e => {
    const b = e.target.closest('[data-quick-add]');
    if (!b) return;
    e.preventDefault();
    b.disabled = true;
    SermentDrawer.add(b.dataset.variantId, 1)
      .then(() => SermentDrawer.open())
      .catch(() => { window.location.href = sermentRoot() + 'cart'; })
      .finally(() => { b.disabled = false; });
  });

  /* — Upsell: one-click add (drawer + product page) — */
  document.addEventListener('click', e => {
    const b = e.target.closest('[data-upsell-add]');
    if (!b) return;
    e.preventDefault();
    b.disabled = true;
    SermentDrawer.add(b.dataset.upsellAdd, 1)
      .then(() => SermentDrawer.open())
      .catch(() => {})
      .finally(() => { b.disabled = false; });
  });

  /* — Product form: AJAX + drawer — */
  const productForm = document.getElementById('product-form');
  if (productForm) {
    productForm.addEventListener('submit', e => {
      e.preventDefault(); const __bn=productForm.dataset.buyNow==="1"; productForm.dataset.buyNow="";
      const btn = productForm.querySelector('[type="submit"]');
      btn.disabled = true;
      const fdp = new FormData(productForm);
      (fdp.get('properties[_gift_acc]') ? SermentGift.accessoryProps(false) : Promise.resolve({}))
        .then(props => { Object.keys(props).forEach(k => fdp.set('properties[' + k + ']', props[k])); return fetch('/cart/add.js', { method: 'POST', body: fdp }); })
        .then(r => { if (!r.ok) throw new Error(); return r.json(); })
        .then(() => {
          const gb = document.querySelector('[data-gift]');
          const gid = gb && parseInt(gb.dataset.selectedGift || '0', 10);
          return null;
        })
        .then(()=>{ if(__bn){ window.location.href=sermentRoot()+"checkout"; return; } SermentDrawer.open(); }).catch(()=>productForm.submit())
        .finally(() => { btn.disabled = false; });
    });
  }

  /* — Option pills: logical size order, "custom" last, colour dots by name — */
  (function () {
    const SIZE_WORDS = { xxs: 0, xs: 1, s: 2, m: 3, l: 4, xl: 5, xxl: 6, '2xl': 6, xxxl: 7, '3xl': 7, '4xl': 8, '5xl': 9, '6xl': 10, '7xl': 11, '8xl': 12 };
    const sizeKey = v => {
      const s = v.toLowerCase().replace(/^.*\|/, '').replace(/\s+/g, '').replace(/\(.*?\)/g, '');
      if (s.indexOf('custom') !== -1) return [9, 0, s];
      const m = s.match(/^(?:us)?(\d+(?:\.\d+)?)(w)?$/);
      if (m) return [m[2] ? 2 : 1, parseFloat(m[1]), s];
      if (SIZE_WORDS[s] !== undefined) return [3, SIZE_WORDS[s], s];
      const m2 = s.match(/^(\d+(?:\.\d+)?)(cm|in|m)$/);
      if (m2) return [4, parseFloat(m2[1]), s];
      return [5, 0, s];
    };
    const COLOURS = [
      /* French names (the colours show in French on /fr) */
      ['blanc cassé', '#F6F1E7'], ['ivoire', '#FBF7E4'], ['crème', '#FFF6D5'], ['blanc', '#FFFFFF'], ['champagne clair', '#F8EBD3'], ['rose poudré', '#D9A3A8'], ['vieux rose', '#D9A3A8'], ['rose pâle', '#F9D5E0'], ['rose clair', '#F9D5E0'], ['pêche', '#F9C9A6'], ['abricot', '#F7C59F'], ['corail', '#F08070'], ['rouge', '#C8102E'], ['lavande', '#C8B4E0'], ['lilas', '#C8A2C8'], ['bleu marine', '#1F2A5C'], ['bleu ciel', '#9FCBEA'], ['bleu roi', '#2B4BC9'], ['bleu clair', '#A9CCE8'], ['bleu', '#3B5BA5'], ['vert sauge', '#9CAF88'], ['émeraude', '#0F5F45'], ['menthe', '#A8E4C9'], ['vert', '#2E7D4F'], ['jaune', '#F2D74E'], ['doré', '#C9A227'], ['argent', '#C8C8CC'], ['gris', '#8A8F96'], ['noir', '#111111'], ['chocolat', '#4E2A14'], ['marron', '#6B4A2B'], ['moutarde', '#D4A017'], ['écru', '#F3EBD8'], ['perle', '#F2F0EB'], ['sable', '#E9DCC5'], ['prune', '#5B2A5B'],
      ['off white', '#F6F1E7'], ['offwhite', '#F6F1E7'], ['ivory', '#FBF7E4'], ['cream', '#FFF6D5'], ['white', '#FFFFFF'],
      ['light champagne', '#F8EBD3'], ['champagne', '#F1DDB0'], ['beige', '#E6D8C3'], ['nude', '#E3C4A8'], ['camel', '#C19A6B'], ['taupe', '#8B8378'], ['khaki', '#C3B091'],
      ['blush', '#F3C6C6'], ['light pink', '#F9D5E0'], ['hot pink', '#FF5FA2'], ['dusty pink', '#D9A3A8'], ['dusty rose', '#D9A3A8'], ['rose gold', '#E0BFB8'], ['rose', '#E48CA0'], ['pink', '#F2B3C6'], ['peach', '#F9C9A6'], ['apricot', '#F7C59F'], ['coral', '#F08070'],
      ['burgundy', '#6D1F2C'], ['bordeaux', '#6D1F2C'], ['wine', '#722F37'], ['dark red', '#8B0000'], ['red', '#C8102E'],
      ['lavender', '#C8B4E0'], ['lilac', '#C8A2C8'], ['dark purple', '#4B2C6F'], ['purple', '#7B3F9E'], ['violet', '#7B3F9E'], ['mauve', '#B784A7'],
      ['dusty blue', '#8FA8C8'], ['light blue', '#A9CCE8'], ['sky blue', '#9FCBEA'], ['royal blue', '#2B4BC9'], ['navy', '#1F2A5C'], ['dark blue', '#1F2A5C'], ['blue', '#3B5BA5'], ['teal', '#2AA7A0'], ['turquoise', '#40C4C4'],
      ['dark green', '#0F5F45'], ['emerald', '#0F5F45'], ['sage', '#9CAF88'], ['mint', '#A8E4C9'], ['olive', '#7A7A3B'], ['green', '#2E7D4F'],
      ['yellow', '#F2D74E'], ['lemon', '#F7E86B'], ['gold', '#C9A227'], ['silver', '#C8C8CC'], ['grey', '#8A8F96'], ['gray', '#8A8F96'], ['charcoal', '#3A3A3C'], ['black', '#111111'],
      ['chocolate', '#4E2A14'], ['coffee', '#6F4E37'], ['brown', '#6B4A2B'], ['orange', '#E8842B'], ['mustard', '#D4A017'],
      ['ecru', '#F3EBD8'], ['pearl', '#F2F0EB'], ['sand', '#E9DCC5'], ['oyster', '#E8E1D5'], ['platinum', '#E5E4E2'], ['rust', '#B7410E'], ['terracotta', '#C4633B'],
      ['plum', '#5B2A5B'], ['fuchsia', '#E0218A'], ['magenta', '#E0218A'], ['aqua', '#7FDBDA'], ['forest', '#1B4D3E'], ['lime', '#B9E36B'], ['blue grey', '#A2B0C3'], ['blue gray', '#A2B0C3'], ['steel', '#7A8BA0']
    ];
    const colourHex = v => {
      const s = v.toLowerCase();
      if (/custom|picture|photo|same as|as shown|multi/.test(s)) return null;
      let best = null;
      COLOURS.forEach(([k, hex]) => { if (s.indexOf(k) !== -1 && (!best || k.length > best[0].length)) best = [k, hex]; });
      return best ? best[1] : null;
    };
    window.SermentColourHex = colourHex;
    document.querySelectorAll('[data-opt-group]').forEach(g => {
      const name = (g.previousElementSibling ? g.previousElementSibling.textContent : '').toLowerCase();
      const pills = Array.from(g.querySelectorAll('.v-pill'));
      if (/size|taille/.test(name)) {
        pills.map(p => ({ p, k: sizeKey(p.querySelector('input').value) }))
          .sort((a, b) => a.k[0] - b.k[0] || a.k[1] - b.k[1] || a.k[2].localeCompare(b.k[2]))
          .forEach(x => g.appendChild(x.p));
      }
      if (/colou?r|couleur/.test(name)) {
        pills.forEach(p => {
          const inp = p.querySelector('input');
          const hex = colourHex(inp.value);
          if (hex && !p.querySelector('.dot')) {
            const d = document.createElement('span');
            d.className = 'dot'; d.style.background = hex; d.setAttribute('aria-hidden', 'true');
            inp.insertAdjacentElement('afterend', d);
          }
        });
      }
    });
  })();

  /* — Variant option groups (Color / Size…) + price — */
  const vSelect = document.querySelector('[data-variant-select]');
  const optGroups = Array.from(document.querySelectorAll('[data-opt-group]'));
  if (vSelect && optGroups.length) {
    const opts = Array.from(vSelect.options).map(o => ({
      el: o, values: (o.dataset.options || '').split('|||'), available: o.dataset.available === 'true'
    }));
    const current = () => optGroups.map(g => { const c = g.querySelector('input:checked'); return c ? c.value : null; });
    const applyPrice = (o) => {
      const badgeTpl = (document.body.dataset.refBadge || '');
      const badge = (o.dataset.compare && o.dataset.savePct && badgeTpl)
        ? ` <span class="pdp-badge">${badgeTpl.replace('[percent]', o.dataset.savePct)}</span>` : '';
      const html = o.dataset.compare
        ? `${o.dataset.price} <span class="old">${o.dataset.compare}</span>${badge}`
        : o.dataset.price;
      const priceEl = document.querySelector('[data-price]');
      if (priceEl) priceEl.innerHTML = html;
      const sPrice = document.querySelector('.sticky-atc .s-price');
      if (sPrice) sPrice.textContent = o.dataset.price;
      const eco = document.querySelector('[data-economy]');
      if (eco) {
        const txt = eco.querySelector('[data-economy-text]');
        if (o.dataset.save && txt) {
          txt.textContent = (eco.dataset.tpl || 'You save [amount] (−[percent]%)')
            .replace('[amount]', o.dataset.save)
            .replace('[percent]', o.dataset.savePct || '');
          eco.classList.remove('hidden');
        } else {
          eco.classList.add('hidden');
        }
      }
    };
    const refresh = () => {
      let sel = current();
      let match = opts.find(o => o.values.every((v, i) => v === sel[i]));
      if (!match) {
        match = opts.find(o => o.values[0] === sel[0] && o.available)
             || opts.find(o => o.values[0] === sel[0])
             || opts.find(o => o.available) || opts[0];
        optGroups.forEach((g, i) => {
          g.querySelectorAll('input').forEach(r => { r.checked = (r.value === match.values[i]); });
        });
        sel = current();
      }
      vSelect.value = match.el.value;
      optGroups.forEach((g, i) => {
        g.querySelectorAll('.v-pill').forEach(p => {
          const inp = p.querySelector('input');
          p.classList.toggle('checked', inp.checked);
          const test = sel.slice(); test[i] = inp.value;
          const ok = opts.some(o => o.available && o.values.every((v, j) => v === test[j]));
          p.classList.toggle('soldout', !ok);
        });
      });
      applyPrice(match.el);
      const btn = document.querySelector('#product-form [type="submit"]');
      if (btn) btn.disabled = !match.available;
      vSelect.dispatchEvent(new Event('change', { bubbles: true }));
    };
    optGroups.forEach(g => g.addEventListener('change', refresh));
    refresh();
  }

  /* — Quantity selector — */
  const qtyBox = document.querySelector('.buy-row .qty');
  if (qtyBox) {
    const input = qtyBox.querySelector('input[name="quantity"]');
    qtyBox.querySelector('[data-qty-minus]').addEventListener('click', () => {
      input.value = Math.max(1, parseInt(input.value || 1, 10) - 1);
    });
    qtyBox.querySelector('[data-qty-plus]').addEventListener('click', () => {
      input.value = Math.min(9, parseInt(input.value || 1, 10) + 1);
    });
  }

  /* — Sticky buy bar (mobile + desktop per setting) — */
  const stickyBar = document.getElementById('stickyAtc');
  if (stickyBar && productForm) {
    const buyRow = document.querySelector('.buy-row');
    const sio = new IntersectionObserver(entries => {
      stickyBar.classList.toggle('show', !entries[0].isIntersecting);
    }, { threshold: 0 });
    if (buyRow) sio.observe(buyRow);
    stickyBar.querySelector('[data-sticky-add]').addEventListener('click', ()=>{ const bn=document.querySelector("[data-buy-now]"); (bn||productForm.querySelector('[type="submit"]')).click(); });
  }

  /* — Product gallery: thumbs + arrows + keyboard + swipe — */
  const gSlides = Array.from(document.querySelectorAll('.g-slide'));
  const gThumbs = Array.from(document.querySelectorAll('.g-thumb'));
  if (gSlides.length) {
    let gIdx = Math.max(0, gSlides.findIndex(s => s.classList.contains('on')));
    const gCount = document.querySelector('[data-g-count]');
    const goSlide = i => {
      gIdx = (i + gSlides.length) % gSlides.length;
      gSlides.forEach((s, k) => s.classList.toggle('on', k === gIdx));
      gThumbs.forEach(t => t.classList.toggle('on', parseInt(t.dataset.slide, 10) === gIdx));
      if (gCount) gCount.textContent = (gIdx + 1) + ' / ' + gSlides.length;
    };
    gThumbs.forEach(th => th.addEventListener('click', () => goSlide(parseInt(th.dataset.slide, 10))));
    const gPrev = document.querySelector('[data-g-prev]');
    const gNext = document.querySelector('[data-g-next]');
    if (gPrev) gPrev.addEventListener('click', () => goSlide(gIdx - 1));
    if (gNext) gNext.addEventListener('click', () => goSlide(gIdx + 1));
    const gMain = document.querySelector('.g-main');
    if (gMain && gSlides.length > 1) {
      gMain.setAttribute('tabindex', '0');
      gMain.addEventListener('keydown', e => {
        if (e.key === 'ArrowLeft') { goSlide(gIdx - 1); e.preventDefault(); }
        if (e.key === 'ArrowRight') { goSlide(gIdx + 1); e.preventDefault(); }
      });
      let tx = null;
      gMain.addEventListener('touchstart', e => { tx = e.touches[0].clientX; }, { passive: true });
      gMain.addEventListener('touchend', e => {
        if (tx === null) return;
        const dx = e.changedTouches[0].clientX - tx;
        if (Math.abs(dx) > 40) goSlide(dx < 0 ? gIdx + 1 : gIdx - 1);
        tx = null;
      }, { passive: true });
    }
  }

  /* — Cart page: +/− submit the form — */
  const cartForm = document.getElementById('cartForm');
  if (cartForm) {
    cartForm.addEventListener('click', e => {
      const minus = e.target.closest('[data-line-minus]');
      const plus = e.target.closest('[data-line-plus]');
      if (!minus && !plus) return;
      const line = e.target.closest('.c-line');
      const input = line.querySelector('[data-line-qty]');
      const v = parseInt(input.value || 0, 10);
      input.value = minus ? Math.max(0, v - 1) : v + 1;
      cartForm.submit();
    });
    cartForm.querySelectorAll('[data-line-qty]').forEach(inp =>
      inp.addEventListener('change', () => cartForm.submit()));
  }

  /* — Collection sorting — */
  const sortSel = document.querySelector('[data-sort]');
  if (sortSel) {
    sortSel.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSel.value);
      window.location.href = url.toString();
    });
  }

  /* — Countdowns + delivery dates + custom size — */
  sermentCountdowns();
  sermentDelivery();
  sermentCustomSize();

  /* — Product page tabs — */
  document.querySelectorAll('.pdp-tabs').forEach(tabs => {
    const heads = tabs.querySelectorAll('.tab-heads button');
    const panels = tabs.querySelectorAll('.tab-panel');
    heads.forEach((h, i) => h.addEventListener('click', () => {
      heads.forEach(x => x.classList.remove('on'));
      panels.forEach(x => x.classList.remove('on'));
      h.classList.add('on');
      if (panels[i]) panels[i].classList.add('on');
    }));
  });

  /* — Size guide (modal) — */
  const sgBtn = document.getElementById('sizeGuideBtn');
  const sgOverlay = document.getElementById('sizeGuideOverlay');
  if (sgBtn && sgOverlay) {
    sgBtn.addEventListener('click', () => sgOverlay.classList.add('open'));
    sgOverlay.addEventListener('click', e => {
      if (e.target === sgOverlay || e.target.closest('.sm-close')) sgOverlay.classList.remove('open');
    });
    document.addEventListener('keydown', e => { if (e.key === 'Escape') sgOverlay.classList.remove('open') });
  }

  /* — Description: Read more / Read less — */
  const descBox = document.querySelector('[data-desc]');
  if (descBox) {
    const inner = descBox.querySelector('[data-desc-inner]');
    const tg = descBox.querySelector('[data-desc-toggle]');
    if (inner.scrollHeight <= inner.clientHeight + 24) { descBox.classList.remove('pdp-desc--clamp'); if (tg) tg.hidden = true; }
    else if (tg) tg.addEventListener('click', () => {
      const open = descBox.classList.toggle('open');
      tg.textContent = open ? tg.dataset.less : tg.dataset.more;
      tg.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (!open) descBox.scrollIntoView({ block: 'start', behavior: 'smooth' });
    });
  }


  /* — Complimentary accessory: add gift (+ the gown if it is not in the cart yet) — */
  document.addEventListener('click', async e => {
    const btn = e.target.closest('[data-gift-add]');
    if (!btn) return;
    const giftId = parseInt(btn.dataset.giftAdd, 10);
    const pid = parseInt((btn.closest('[data-gift]') || {}).dataset.productId || '0', 10);
    btn.disabled = true;
    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      const gownIn = cart.items.some(i => i.product_id === pid);
      const items = [{ id: giftId, quantity: 1, properties: await SermentGift.accessoryProps(false) }];
      if (!gownIn) {
        const form = document.getElementById('product-form');
        const fd = form ? new FormData(form) : null;
        const vid = fd ? parseInt(fd.get('id'), 10) : 0;
        if (vid) {
          const props = {};
          if (fd) for (const [k, v] of fd.entries()) { const m = k.match(/^properties\[(.+)\]$/); if (m && v) props[m[1]] = v; }
          items.unshift({ id: vid, quantity: 1, properties: props });
        }
      }
      const r = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items }) });
      if (!r.ok) throw new Error();
      SermentDrawer.open();
    } catch (e) { window.location.href = sermentRoot() + 'cart'; }
    finally { btn.disabled = false; }
  });

  /* — Gown page: accessories drawn at random on every load (+ ?gift=handle pre-selects one) — */
  const giftBox = document.querySelector('[data-gift]');
  if (giftBox) {
    const grid = giftBox.querySelector('[data-gift-grid]');
    const n = parseInt(giftBox.dataset.giftLimit || '4', 10);
    const pools = (giftBox.dataset.giftPools || 'gift-accessories').split(',').map(x => x.trim()).filter(Boolean);
    const card = (p, sel) => { const v = SermentGift.variant(p); return `
      <div class="gift-card${sel ? ' is-selected' : ''}" data-handle="${p.handle}">
        <a class="gift-visual t-stone" href="${sermentRoot()}products/${p.handle}">${SermentGift.img(p, 500) ? `<img src="${SermentGift.img(p, 500)}" alt="${sermentEsc(p.title)}" loading="lazy" width="500" height="625">` : ''}</a>
        <div class="gift-name">${sermentEsc(p.title)}</div>
        <div class="gift-price"><s>${sermentFmt(SermentGift.cents(p))}</s> <span>${giftBox.dataset.freeLabel || ''}</span></div>
        <button type="button" class="gift-add" data-gift-add="${v.id}">${sel ? (giftBox.dataset.selLabel || giftBox.dataset.btnLabel || '') : (giftBox.dataset.btnLabel || '')}</button>
      </div>`; };
    const pre = new URLSearchParams(location.search).get('gift');
    (async () => {
      const [list, preP] = await Promise.all([
        SermentGift.fetchPool(pools, n, 2),
        pre ? fetch(`/products/${encodeURIComponent(pre)}.js`).then(r => r.ok ? r.json() : null).catch(() => null) : null
      ]);
      let out = list;
      if (preP && preP.variants) {
        out = [preP].concat(list.filter(p => p.handle !== preP.handle)).slice(0, n);
        giftBox.dataset.selectedGift = SermentGift.variant(preP).id;
      }
      if (grid && out.length) grid.innerHTML = out.map((p, i) => card(p, !!(preP && i === 0))).join('');
      if (preP) giftBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
    })();
  }

  /* — Accessory page: gowns drawn at random, links carry ?gift=handle — */
  const accBox = document.querySelector('[data-gift-acc]');
  if (accBox) {
    const grid = accBox.querySelector('[data-gown-grid]');
    const n = parseInt(accBox.dataset.giftLimit || '4', 10);
    const gh = accBox.dataset.giftHandle;
    SermentGift.fetchPool([accBox.dataset.gownCollection || 'wedding-dress'], n, 16).then(list => {
      if (!grid || !list.length) return;
      grid.innerHTML = list.map(p => { const url = `/products/${p.handle}?gift=${encodeURIComponent(gh)}`; return `
        <div class="gift-card" data-handle="${p.handle}">
          <a class="gift-visual t-stone" href="${url}">${SermentGift.img(p, 500) ? `<img src="${SermentGift.img(p, 500)}" alt="${sermentEsc(p.title)}" loading="lazy" width="500" height="625">` : ''}</a>
          <div class="gift-name">${sermentEsc(p.title)}</div>
          <div class="gift-price">${sermentFmt(SermentGift.cents(p))}</div>
          <a class="gift-add" href="${url}">${accBox.dataset.btnLabel || ''}</a>
        </div>`; }).join('');
    }).catch(() => {});
  }

  /* — Size guide: zoom lightbox with arrows — */
  const sgLb = document.querySelector('[data-sg-lightbox]');
  const sgZooms = Array.from(document.querySelectorAll('[data-sg-zoom]'));
  if (sgLb && sgZooms.length) {
    const lbImg = sgLb.querySelector('[data-sg-lb-img]');
    const lbCount = sgLb.querySelector('[data-sg-lb-count]');
    let li = 0;
    const showLb = i => {
      li = (i + sgZooms.length) % sgZooms.length;
      const z = sgZooms[li];
      lbImg.src = z.dataset.full;
      lbImg.alt = z.getAttribute('aria-label') || '';
      sgLb.classList.remove('zoomed');
      if (lbCount) lbCount.textContent = (li + 1) + ' / ' + sgZooms.length;
    };
    const openLb = i => { showLb(i); sgLb.hidden = false; document.body.classList.add('csm-lock'); };
    const closeLb = () => { sgLb.hidden = true; document.body.classList.remove('csm-lock'); };
    sgZooms.forEach((z, i) => z.addEventListener('click', () => openLb(i)));
    sgLb.querySelector('[data-sg-lb-close]').addEventListener('click', closeLb);
    const lbPrev = sgLb.querySelector('[data-sg-lb-prev]');
    const lbNext = sgLb.querySelector('[data-sg-lb-next]');
    if (lbPrev) lbPrev.addEventListener('click', () => showLb(li - 1));
    if (lbNext) lbNext.addEventListener('click', () => showLb(li + 1));
    lbImg.addEventListener('click', () => sgLb.classList.toggle('zoomed'));
    sgLb.addEventListener('click', e => { if (e.target === sgLb) closeLb(); });
    document.addEventListener('keydown', e => {
      if (sgLb.hidden) return;
      if (e.key === 'Escape') closeLb();
      if (e.key === 'ArrowLeft') showLb(li - 1);
      if (e.key === 'ArrowRight') showLb(li + 1);
    });
    if (sgZooms.length < 2) sgLb.classList.add('single');
  }

  /* — Before / After — */
  document.querySelectorAll('.ba-frame').forEach(frame => {
    const range = frame.querySelector('.ba-range');
    if (!range) return;
    range.addEventListener('input', () => frame.style.setProperty('--ba-pos', range.value + '%'));
  });

  /* — Bundle offer: add everything — */
  document.querySelectorAll('[data-bundle-add]').forEach(btn => {
    btn.addEventListener('click', () => {
      let ids;
      try { ids = JSON.parse(btn.dataset.bundleAdd) } catch(e) { return }
      if (!ids.length) return;
      const msg = btn.closest('.bundle-sum') ? btn.closest('.bundle-sum').querySelector('.bundle-msg') : null;
      btn.disabled = true;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: ids.map(id => ({ id: id, quantity: 1 })) })
      })
        .then(r => { if (!r.ok) throw new Error(); return r.json(); })
        .then(() => { if (msg){ msg.textContent = 'Look added to cart ✓'; msg.className = 'bundle-msg ok'; } SermentDrawer.open(); })
        .catch(() => { if (msg){ msg.textContent = 'One item in the bundle is unavailable.'; msg.className = 'bundle-msg err'; } })
        .finally(() => { btn.disabled = false; });
    });
  });

  /* — Hotspots (mobile tap) — */
  document.querySelectorAll('.hs-dot').forEach(dot => {
    dot.addEventListener('click', e => {
      if (e.target.closest('.t-link')) return;
      document.querySelectorAll('.hs-dot').forEach(d => { if (d !== dot) d.classList.remove('on') });
      dot.classList.toggle('on');
    });
  });

  /* — Reviews carousel — */
  document.querySelectorAll('.rc-wrap').forEach(wrap => {
    const track = wrap.querySelector('.rc-track');
    if (!track) return;
    const step = () => {
      const card = track.querySelector('.rc-card');
      return card ? card.getBoundingClientRect().width + 16 : 320;
    };
    wrap.querySelectorAll('[data-rc-prev]').forEach(b => b.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' })));
    wrap.querySelectorAll('[data-rc-next]').forEach(b => b.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' })));
    if (track.dataset.auto === 'true') {
      setInterval(() => {
        if (wrap.matches(':hover')) return;
        const max = track.scrollWidth - track.clientWidth - 4;
        if (track.scrollLeft >= max) track.scrollTo({ left: 0, behavior: 'smooth' });
        else track.scrollBy({ left: step(), behavior: 'smooth' });
      }, 4500);
    }
  });

  /* — Newsletter popup (7-day cookie) — */
  const np = document.getElementById('npOverlay');
  if (np) {
    const delay = parseInt(np.dataset.delay || '6', 10) * 1000;
    const seen = document.cookie.split(';').some(c => c.trim().startsWith('serment_np=1'));
    const dismiss = () => {
      np.classList.remove('open');
      document.cookie = 'serment_np=1; max-age=' + (7 * 86400) + '; path=/';
    };
    if (!seen) setTimeout(() => np.classList.add('open'), delay);
    np.addEventListener('click', e => {
      if (e.target === np || e.target.closest('.np-close')) dismiss();
    });
    const npForm = np.querySelector('form');
    if (npForm) npForm.addEventListener('submit', () =>
      document.cookie = 'serment_np=1; max-age=' + (7 * 86400) + '; path=/');
  }

  /* — Mega menu: keyboard/touch toggle — */
  document.querySelectorAll('.has-mega > a').forEach(a => {
    a.addEventListener('click', e => {
      if (window.matchMedia('(hover: none)').matches && window.innerWidth > 880) {
        e.preventDefault();
        a.parentElement.classList.toggle('open');
      }
    });
  });
});

/* — Theme editor: re-initialize when a section is reloaded — */
document.addEventListener('shopify:section:load', () => {
  document.body.classList.remove('csm-lock');
  sermentCustomSize();
  sermentDelivery();
});

/* ═══ Serment sizing: US + EU + letter on the size pills, "Find my size" helper, brand size table ═══
   Display layer only: variants, prices and the cart are untouched. If anything is missing, it does nothing. */
(function(){
  const FR = /^fr/i.test(document.documentElement.lang || '');
  const T = FR ? {
    label: 'Taille', legend: 'Taille US · équivalence européenne en dessous',
    plus: 'Grandes tailles', custom: 'Sur mesure',
    find: 'Trouver ma taille', close: 'Fermer',
    title: 'Trouver ma taille',
    intro: 'Indiquez vos mesures, prises à même le corps avec un mètre ruban souple. Nous vous suggérons une ou deux tailles d’après notre guide&nbsp;: c’est vous qui choisissez.',
    bust: 'Poitrine', waist: 'Tour de taille', hips: 'Hanches',
    hBust: 'à l’endroit le plus fort', hWaist: 'à votre taille naturelle, au plus fin', hHips: 'à l’endroit le plus large',
    go: 'Voir ma suggestion', err: 'Merci d’indiquer vos trois mesures.',
    errRange: 'Une mesure semble hors des valeurs habituelles : vérifiez-la.',
    sugg: 'Suggérée', alt: 'Possible',
    one: s => `D’après vos mesures, le <b>${s}</b> est la taille la plus proche.`,
    two: (a, b) => `Vos mesures se situent entre le <b>${a}</b> et le <b>${b}</b>. Nous suggérons le <b>${b}</b>, la plus grande des deux&nbsp;: une robe se reprend plus facilement qu’elle ne s’élargit.`,
    many: s => `Vos mesures correspondent à plusieurs tailles. Le <b>${s}</b> est la taille standard qui les couvre toutes, mais pour cette silhouette le sur-mesure sera plus juste.`,
    over: 'Vos mesures dépassent les tailles standard proposées pour cette robe&nbsp;: le sur-mesure est fait pour vous.',
    per: 'Détail', to: '→',
    note: 'Suggestion indicative, calculée à partir de notre guide des tailles&nbsp;: elle ne remplace pas vos mesures. Un écart de 1 à 2&nbsp;cm entre deux tailles est normal. En cas de doute, choisissez la plus grande ou le sur-mesure.',
    select: s => `Choisir le ${s}`, toCustom: 'Commander sur mesure avec ces mesures',
    tbl: 'Guide des tailles', tblSub: 'Mesures du corps, en centimètres ou en pouces.',
    cm: 'cm', inch: 'pouces', size: 'Taille', height: 'Hauteur', hollow: 'Creux du cou à l’ourlet', tWaist: 'Taille',
    how: 'Comment mesurer', howTxt: ['<b>Poitrine</b> : autour de la partie la plus forte de la poitrine.', '<b>Taille</b> : autour de votre taille naturelle.', '<b>Hanches</b> : autour de la partie la plus large des hanches.', '<b>Creux du cou à l’ourlet</b> : pieds nus, du creux entre les clavicules jusqu’au sol.'],
    illustrated: 'Voir le guide illustré', hideIll: 'Masquer le guide illustré',
    wa: 'Demander conseil sur WhatsApp', waMsg: (p, b, w, h, s) => `Bonjour, pour la robe « ${p} » : poitrine ${b}, tour de taille ${w}, hanches ${h}.${s ? ' L’outil me suggère le ' + s + '.' : ''} Pouvez-vous me conseiller sur la taille ?`
  } : {
    label: 'Size', legend: 'US size · European equivalent below',
    plus: 'Plus sizes', custom: 'Custom size',
    find: 'Find my size', close: 'Close',
    title: 'Find my size',
    intro: 'Enter your body measurements, taken with a soft tape measure. We suggest one or two sizes from our size guide: the choice is always yours.',
    bust: 'Bust', waist: 'Waist', hips: 'Hips',
    hBust: 'around the fullest part', hWaist: 'around your natural waistline', hHips: 'around the widest part',
    go: 'See my suggestion', err: 'Please enter all three measurements.',
    errRange: 'One measurement looks unusual: please check it.',
    sugg: 'Suggested', alt: 'Possible',
    one: s => `From your measurements, <b>${s}</b> is the closest size.`,
    two: (a, b) => `Your measurements fall between <b>${a}</b> and <b>${b}</b>. We suggest <b>${b}</b>, the larger of the two: a gown is easier to take in than to let out.`,
    many: s => `Your measurements match several sizes. <b>${s}</b> is the standard size that covers them all, but for your figure a custom size will fit better.`,
    over: 'Your measurements are beyond the standard sizes offered for this gown: a custom size is made for you.',
    per: 'Detail', to: '→',
    note: 'A guide only, based on our size chart: it does not replace your measurements. A 1 to 2 cm gap between two sizes is normal. If in doubt, choose the larger size or a custom size.',
    select: s => `Choose ${s}`, toCustom: 'Order a custom size with these measurements',
    tbl: 'Size guide', tblSub: 'Body measurements, in centimetres or inches.',
    cm: 'cm', inch: 'inches', size: 'Size', height: 'Height', hollow: 'Hollow to hem',
    how: 'How to measure', howTxt: ['<b>Bust</b>: around the fullest part of your bust.', '<b>Waist</b>: around your natural waistline.', '<b>Hips</b>: around the fullest part of your hips.', '<b>Hollow to hem</b>: barefoot, from the hollow between your collarbones to the floor.'],
    illustrated: 'See the illustrated guide', hideIll: 'Hide the illustrated guide',
    wa: 'Ask our team on WhatsApp', waMsg: (p, b, w, h, s) => `Hello, about the “${p}”: bust ${b}, waist ${w}, hips ${h}.${s ? ' The size helper suggests ' + s + '.' : ''} Could you advise me on the size?`
  };

  /* Serment size chart (same values as the published chart) */
  const CHART = [
    // key, letter, UK, EU, bust, waist, hips, hollow, height (cm)
    ['2','S',6,32,83,65,91,147,160], ['4','S',8,34,84,68,92,147,160],
    ['6','M',10,36,88,70,96,150,165], ['8','M',12,38,90,72,98,150,165],
    ['10','L',14,40,93,75,101,152,170], ['12','L',16,42,97,79,105,152,170],
    ['14','XL',18,44,100,83,109,155,175], ['16','XL',20,46,104,86,112,155,175],
    ['14w','',18,44,104,86,110,155,175], ['16w','',20,46,109,92,116,155,175], ['18w','',22,48,114,98,121,155,175],
    ['20w','',24,50,119,104,126,155,175], ['22w','',26,52,124,109,131,155,175], ['24w','',28,54,130,115,136,155,175], ['26w','',30,56,135,121,141,155,175]
  ];
  const ROW = {}; CHART.forEach(r => { ROW[r[0]] = r; });
  const norm = v => String(v || '').toLowerCase().replace(/^.*\|/, '').replace(/^us\s*/, '').replace(/\s+/g, '');
  const disp = k => k.replace('w', 'W');
  const nameOf = k => { const r = ROW[k]; return r ? `${disp(k)} (EU ${r[3]}${r[1] ? ' · ' + r[1] : ''})` : disp(k); };

  function init(){
    const groups = Array.from(document.querySelectorAll('[data-opt-group]'));
    const g = groups.find(x => {
      const ins = Array.from(x.querySelectorAll('input'));
      return ins.filter(i => ROW[norm(i.value)]).length >= 4;
    });
    if (!g || g.dataset.szReady) return;
    g.dataset.szReady = '1';

    /* use the product's own measurements when the custom-size block provides them */
    const cs = document.querySelector('[data-custom-size]');
    try {
      const sp = JSON.parse((cs && cs.dataset.sizeSpecs) || '{}');
      Object.keys(sp).forEach(k => { const r = ROW[k]; const v = sp[k]; if (r && Array.isArray(v) && v.length >= 4) { for (let i = 0; i < 4; i++) { const n = parseFloat(v[i]); if (!isNaN(n)) r[4 + i] = n; } } });
    } catch (e) {}

    /* ── 1. Pills: letter groups + EU equivalent ── */
    const pills = Array.from(g.querySelectorAll('.v-pill'));
    const info = p => { const i = p.querySelector('input'); return i ? norm(i.value) : ''; };
    const sized = pills.filter(p => ROW[info(p)]);
    const others = pills.filter(p => !ROW[info(p)]);
    const lbl = g.previousElementSibling && g.previousElementSibling.classList.contains('opt-label') ? g.previousElementSibling : null;
    if (lbl) { lbl.classList.add('sz-label'); lbl.innerHTML = `<span>${T.label}</span><small>${T.legend}</small>`; }
    sized.forEach(p => {
      const r = ROW[info(p)];
      if (p.querySelector('.sz-eu')) return;
      const inp = p.querySelector('input');
      const num = document.createElement('span'); num.className = 'sz-num'; num.textContent = disp(r[0]);
      const eu = document.createElement('small'); eu.className = 'sz-eu'; eu.textContent = 'EU ' + r[3];
      Array.from(p.childNodes).forEach(n => { if (n !== inp && n.nodeType === 3) n.textContent = ''; });
      p.appendChild(num); p.appendChild(eu);
      p.dataset.sz = r[0];
    });
    const wrap = document.createElement('div'); wrap.className = 'sz-groups';
    const mk = (title, sub, list, cls) => {
      if (!list.length) return;
      const box = document.createElement('div'); box.className = 'sz-grp ' + (cls || '');
      box.innerHTML = `<div class="sz-grp-h"><b>${title}</b>${sub ? `<span>${sub}</span>` : ''}</div>`;
      const row = document.createElement('div'); row.className = 'sz-row';
      list.forEach(p => row.appendChild(p));
      box.appendChild(row); wrap.appendChild(box);
    };
    ['S', 'M', 'L', 'XL'].forEach(L => {
      const list = sized.filter(p => ROW[p.dataset.sz][1] === L);
      const eus = list.map(p => ROW[p.dataset.sz][3]);
      mk(L, eus.length ? 'EU ' + (eus.length > 1 ? Math.min(...eus) + '–' + Math.max(...eus) : eus[0]) : '', list);
    });
    const plusList = sized.filter(p => !ROW[p.dataset.sz][1]);
    if (plusList.length) {
      const eus = plusList.map(p => ROW[p.dataset.sz][3]);
      mk(T.plus, 'EU ' + (eus.length > 1 ? Math.min(...eus) + '–' + Math.max(...eus) : eus[0]), plusList, 'sz-plus');
    }
    if (others.length) { const box = document.createElement('div'); box.className = 'sz-other'; others.forEach(p => box.appendChild(p)); wrap.appendChild(box); }
    g.appendChild(wrap);
    g.classList.add('sz-on');

    /* ── 2. Find my size ── */
    const kw = ((cs && cs.dataset.keyword) || '').toLowerCase();
    const customPill = others.find(p => { const v = ((p.querySelector('input') || {}).value || '').toLowerCase(); return (kw && v.indexOf(kw) !== -1) || /custom|mesure|personnalis/.test(v); });
    const bar = document.createElement('div'); bar.className = 'sz-bar';
    bar.innerHTML = `<button type="button" class="sz-find" aria-expanded="false"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M4 12h10M4 17h6"/><circle cx="17.5" cy="15.5" r="3"/><path d="M19.7 17.7 22 20"/></svg>${T.find}</button>`;
    const sgBtn = document.getElementById('sizeGuideBtn');
    if (sgBtn && sgBtn.parentElement) { const row = document.createElement('div'); row.className = 'sz-tools'; sgBtn.insertAdjacentElement('beforebegin', row); row.appendChild(sgBtn); row.appendChild(bar); }
    else g.insertAdjacentElement('afterend', bar);
    const panel = document.createElement('div'); panel.className = 'sz-panel'; panel.hidden = true;
    const f = (id, name, hint) => `<label class="sz-f"><span class="sz-f-n">${name}</span><span class="sz-f-in"><input type="text" inputmode="decimal" autocomplete="off" data-sz-in="${id}" aria-label="${name}"><em data-sz-unit>cm</em></span><span class="sz-f-h">${hint}</span></label>`;
    panel.innerHTML = `
      <div class="sz-p-head"><h4>${T.title}</h4><div class="sz-units" role="group"><button type="button" data-u="cm" class="on">cm</button><button type="button" data-u="in">in</button></div></div>
      <p class="sz-intro">${T.intro}</p>
      <div class="sz-fields">${f('b', T.bust, T.hBust)}${f('w', T.waist, T.hWaist)}${f('h', T.hips, T.hHips)}</div>
      <p class="sz-err" hidden></p>
      <button type="button" class="sz-go">${T.go}</button>
      <div class="sz-res" hidden aria-live="polite"></div>
      <p class="sz-note">${T.note}</p>`;
    g.insertAdjacentElement('afterend', panel);
    const btn = bar.querySelector('.sz-find');
    btn.addEventListener('click', () => {
      panel.hidden = !panel.hidden;
      btn.setAttribute('aria-expanded', String(!panel.hidden));
      btn.classList.toggle('on', !panel.hidden);
      if (!panel.hidden) {
        const i = panel.querySelector('input');
        const r = panel.getBoundingClientRect();
        if (r.top < 90 || r.top > window.innerHeight * 0.6) window.scrollBy({ top: r.top - 110, behavior: 'smooth' });
        setTimeout(() => i && i.focus({ preventScroll: true }), 350);
      }
    });
    let unit = 'cm';
    panel.querySelectorAll('.sz-units button').forEach(b => b.addEventListener('click', () => {
      const nu = b.dataset.u; if (nu === unit) return;
      panel.querySelectorAll('[data-sz-in]').forEach(i => {
        const v = parseFloat(String(i.value).replace(',', '.'));
        if (!isNaN(v)) i.value = String(Math.round((nu === 'in' ? v / 2.54 : v * 2.54) * 2) / 2).replace('.', FR ? ',' : '.');
      });
      unit = nu;
      panel.querySelectorAll('.sz-units button').forEach(x => x.classList.toggle('on', x === b));
      panel.querySelectorAll('[data-sz-unit]').forEach(e => { e.textContent = unit; });
    }));
    const available = sized.map(p => p.dataset.sz);
    const std = CHART.filter(r => r[1] && available.includes(r[0]));
    const plus = CHART.filter(r => !r[1] && available.includes(r[0]));
    const idxFor = (scale, m, col) => { for (let i = 0; i < scale.length; i++) if (scale[i][col] >= m - 1) return i; return -1; };
    const clearReco = () => sized.forEach(p => { p.classList.remove('sz-reco', 'sz-alt'); const t = p.querySelector('.sz-tag'); if (t) t.remove(); });
    const tag = (k, cls, txt) => { const p = sized.find(x => x.dataset.sz === k); if (!p) return; p.classList.add(cls); const t = document.createElement('i'); t.className = 'sz-tag'; t.textContent = txt; p.appendChild(t); };
    const choose = k => { const p = sized.find(x => x.dataset.sz === k); if (p) { const i = p.querySelector('input'); if (i && !i.checked) i.click(); } };
    let last = null;
    panel.querySelector('.sz-go').addEventListener('click', () => {
      const err = panel.querySelector('.sz-err'), res = panel.querySelector('.sz-res');
      const val = id => { const v = parseFloat(String(panel.querySelector(`[data-sz-in="${id}"]`).value).replace(',', '.')); return isNaN(v) ? NaN : (unit === 'in' ? v * 2.54 : v); };
      const m = { b: val('b'), w: val('w'), h: val('h') };
      clearReco(); res.hidden = true; res.dataset.sugg = '';
      if ([m.b, m.w, m.h].some(isNaN)) { err.textContent = T.err; err.hidden = false; return; }
      if (m.b < 60 || m.b > 170 || m.w < 45 || m.w > 170 || m.h < 60 || m.h > 180) { err.textContent = T.errRange; err.hidden = false; return; }
      err.hidden = true;
      let scale = std, ids = [idxFor(std, m.b, 4), idxFor(std, m.w, 5), idxFor(std, m.h, 6)];
      if (ids.some(i => i < 0) && plus.length) { scale = plus; ids = [idxFor(plus, m.b, 4), idxFor(plus, m.w, 5), idxFor(plus, m.h, 6)]; }
      const dec = n => FR ? String(n).replace('.', ',') : String(n);
      const fmt = cm => unit === 'in' ? dec(Math.round(cm / 2.54 * 2) / 2) + ' in' : dec(Math.round(cm * 2) / 2) + ' cm';
      let html = '', actions = '';
      if (!scale.length || ids.some(i => i < 0)) {
        html = `<p>${T.over}</p>`;
      } else {
        const hi = Math.max(...ids), lo = Math.min(...ids);
        const rec = scale[hi][0];
        res.dataset.sugg = nameOf(rec);
        const detail = [[T.bust, m.b, ids[0]], [T.waist, m.w, ids[1]], [T.hips, m.h, ids[2]]]
          .map(([n, v, i]) => `<li><span>${n} ${fmt(v)}</span><b>${disp(scale[i][0])}</b></li>`).join('');
        if (hi === lo) { html = `<p>${T.one(nameOf(rec))}</p>`; tag(rec, 'sz-reco', T.sugg); }
        else if (hi - lo === 1) { const a = scale[lo][0]; html = `<p>${T.two(nameOf(a), nameOf(rec))}</p>`; tag(rec, 'sz-reco', T.sugg); tag(a, 'sz-alt', T.alt); }
        else { html = `<p>${T.many(nameOf(rec))}</p>`; tag(rec, 'sz-reco', T.sugg); }
        html += `<ul class="sz-detail" aria-label="${T.per}">${detail}</ul>`;
        actions += `<button type="button" class="sz-pick" data-k="${rec}">${T.select(disp(rec))}</button>`;
      }
      if (customPill && cs) actions += `<button type="button" class="sz-tocustom">${T.toCustom}</button>`;
      const waA = document.querySelector('a[href*="wa.me/"]'); const waM = waA && waA.getAttribute('href').match(/wa\.me\/(\d+)/);
      if (waM) {
        const h1 = document.querySelector('h1'); const sugg = res.dataset.sugg || '';
        const msg = T.waMsg(h1 ? h1.textContent.trim() : document.title, fmt(m.b), fmt(m.w), fmt(m.h), sugg);
        actions += `<a class="sz-wa" href="https://wa.me/${waM[1]}?text=${encodeURIComponent(msg)}" target="_blank" rel="noopener">${T.wa}</a>`;
      }
      res.innerHTML = html + (actions ? `<div class="sz-actions">${actions}</div>` : '');
      res.hidden = false;
      last = m;
      const pk = res.querySelector('.sz-pick'); if (pk) pk.addEventListener('click', () => choose(pk.dataset.k));
      const tc = res.querySelector('.sz-tocustom'); if (tc) tc.addEventListener('click', toCustom);
    });
    panel.addEventListener('keydown', e => { if (e.key === 'Enter' && e.target.matches('[data-sz-in]')) { e.preventDefault(); panel.querySelector('.sz-go').click(); } });

    /* custom size: select the pill, then pre-fill bust / waist / hips in the existing modal */
    function toCustom(){
      if (!customPill || !last) return;
      const inp = customPill.querySelector('input');
      if (inp && !inp.checked) inp.click(); else if (cs) { const t = cs.querySelector('[data-cs-toggle]'); if (t) t.click(); }
      setTimeout(() => {
        const us = cs.querySelector('[data-cs-unit-select]');
        if (us && us.value !== 'cm') { us.value = 'cm'; us.dispatchEvent(new Event('change', { bubbles: true })); }
        const put = (name, cm) => {
          const s = cs.querySelector(`select[name="properties[${name}]"]`); if (!s) return;
          const want = Math.round(cm) + ' cm';
          if (Array.from(s.options).some(o => o.value === want)) { s.value = want; s.dispatchEvent(new Event('change', { bubbles: true })); }
        };
        put('Bust', last.b); put('Waist', last.w); put('Hips', last.h);
        if (!cs.classList.contains('csm-open')) { const t = cs.querySelector('[data-cs-toggle]'); if (t) t.click(); }
      }, 180);
    }

    /* ── 3. Size guide: brand table (the illustrated chart stays one click away) ── */
    const modal = document.querySelector('#sizeGuideOverlay .size-modal');
    if (modal && !modal.querySelector('.sz-table')) {
      const ill = modal.querySelector('.size-table-wrap');
      const rows = CHART.filter(r => available.includes(r[0]) || r[1]);
      const box = document.createElement('div'); box.className = 'sz-guide';
      const head = `<div class="sz-g-head"><div><h3>${T.tbl}</h3><p>${T.tblSub}</p></div><div class="sz-units" role="group"><button type="button" data-u="cm" class="on">cm</button><button type="button" data-u="in">in</button></div></div>`;
      const cell = cm => `<td data-cm="${cm}">${cm}</td>`;
      const body = rows.map(r => `<tr data-k="${r[0]}"><th scope="row"><b>${disp(r[0])}</b>${r[1] ? `<i>${r[1]}</i>` : ''}</th><td class="sz-c-eu">${r[3]}</td><td class="sz-c-uk">${r[2]}</td>${cell(r[4])}${cell(r[5])}${cell(r[6])}${cell(r[7])}</tr>`).join('');
      box.innerHTML = head + `<div class="sz-t-wrap"><table class="sz-table"><thead><tr><th scope="col">US</th><th scope="col">EU</th><th scope="col" class="sz-c-uk">UK</th><th scope="col">${T.bust}</th><th scope="col">${T.tWaist || T.waist}</th><th scope="col">${T.hips}</th><th scope="col">${T.hollow}</th></tr></thead><tbody>${body}</tbody></table></div>
        <div class="sz-how"><h4>${T.how}</h4><ul>${T.howTxt.map(t => `<li>${t}</li>`).join('')}</ul><p>${T.note}</p></div>`;
      if (ill) {
        const tg = document.createElement('button'); tg.type = 'button'; tg.className = 'sz-ill-t'; tg.textContent = T.illustrated;
        ill.hidden = true; ill.classList.add('sz-ill');
        tg.addEventListener('click', () => { ill.hidden = !ill.hidden; tg.textContent = ill.hidden ? T.illustrated : T.hideIll; });
        ill.insertAdjacentElement('beforebegin', box);
        box.insertAdjacentElement('afterend', tg);
      } else {
        const old = modal.querySelector('.size-table'); if (old) old.closest('.size-table-wrap').hidden = true;
        const close = modal.querySelector('.sm-close'); (close || modal.firstChild).insertAdjacentElement('afterend', box);
      }
      modal.classList.add('sz-modal');
      box.querySelectorAll('.sz-units button').forEach(b => b.addEventListener('click', () => {
        const u = b.dataset.u;
        box.querySelectorAll('.sz-units button').forEach(x => x.classList.toggle('on', x === b));
        box.querySelectorAll('td[data-cm]').forEach(td => { const cm = parseFloat(td.dataset.cm); const v = u === 'in' ? String(Math.round(cm / 2.54 * 2) / 2) : String(cm); td.textContent = FR ? v.replace('.', ',') : v; });
      }));
      const mark = () => {
        const c = g.querySelector('input:checked'); const k = c ? norm(c.value) : '';
        box.querySelectorAll('tbody tr').forEach(tr => tr.classList.toggle('on', tr.dataset.k === k));
      };
      g.addEventListener('change', mark); mark();
    }
  }
  const run = () => { try { init(); } catch (e) { if (window.console) console.warn('sizing', e); } };
  if (document.readyState === 'complete') setTimeout(run, 0); else document.addEventListener('DOMContentLoaded', () => setTimeout(run, 0));
  window.addEventListener('load', run);
})();


/* ═══ Serment funnels: wedding-date check, cart guidance, accessory breadcrumbs, WhatsApp hand-offs ═══
   Display layer only. No offer, no invented promise: every date comes from the delivery window already
   shown on the product page (data-j1 / data-j2). Anything missing -> it does nothing. */
(function(){
  const FR = /^fr/i.test(document.documentElement.lang || '');
  const ROOT = (() => { const r = document.body.dataset.root || '/'; return r.slice(-1) === '/' ? r : r + '/'; })();
  const LOC = FR ? 'fr-FR' : 'en-GB';
  const T = FR ? {
    ask: 'Votre date de mariage ? Vérifier les délais', date: 'Date de votre mariage', check: 'Vérifier',
    ok: (a, b, w) => `Votre commande arriverait entre le <b>${a}</b> et le <b>${b}</b>, soit environ <b>${w} semaines</b> avant votre mariage.`,
    tight: (a, b, d) => `Votre commande arriverait entre le <b>${a}</b> et le <b>${b}</b> : il resterait <b>${d} jours</b> avant votre mariage. C’est faisable, mais écrivez-nous avant de commander pour que nous confirmions ensemble.`,
    late: 'Notre délai de livraison se termine après la date de votre mariage. Écrivez-nous avant de commander : nous vous dirons honnêtement ce qui est possible.',
    past: 'Cette date semble passée : vérifiez-la.',
    note: 'Estimation d’après le délai de livraison indiqué sur cette page, à partir d’une commande passée aujourd’hui.',
    wa: 'Écrire à l’équipe sur WhatsApp', saved: 'Date enregistrée avec votre panier.',
    size: 'Taille', look: 'Compléter la tenue', lookSub: 'Un voile, une cape ou des gants pour parfaire votre robe : coloris et tailles se choisissent sur chaque fiche.', lookAll: 'Voir tous les accessoires', choose: 'Choisir',
    step2: 'Tour de poitrine, tour de taille, tour de hanches, longueur du creux du cou au sol et hauteur des talons, directement sur la page de chaque robe.',
    quizWa: 'Une question sur ces robes ? Demandez conseil sur WhatsApp',
    quizMsg: s => `Bonjour, le quiz de Serment Couture me suggère : ${s}. Pouvez-vous me conseiller ?`,
    dateMsg: (p, d) => `Bonjour, je me marie le ${d} et je pense commander : ${p}. Les délais sont-ils bons ?`,
    veils: 'Voiles', capes: 'Capes', gloves: 'Gants', vowery: 'Vowery', acc: 'Accessoires',
    veilLen: 'Longueur du voile', len: 'Longueur', lenHelp: 'Quelle longueur choisir ?', shadeHelp: 'Blanc, ivoire ou blanc cassé ?'
  } : {
    ask: 'Your wedding date? Check the timing', date: 'Your wedding date', check: 'Check',
    ok: (a, b, w) => `Your order would arrive between <b>${a}</b> and <b>${b}</b>, about <b>${w} weeks</b> before your wedding.`,
    tight: (a, b, d) => `Your order would arrive between <b>${a}</b> and <b>${b}</b>, leaving <b>${d} days</b> before your wedding. It can work, but please write to us before ordering so we confirm it together.`,
    late: 'Our delivery window ends after your wedding date. Please write to us before ordering: we will tell you honestly what is possible.',
    past: 'This date seems to be in the past: please check it.',
    note: 'Estimate based on the delivery window shown on this page, for an order placed today.',
    wa: 'Write to our team on WhatsApp', saved: 'Date saved with your cart.',
    size: 'Size', look: 'Complete the look', lookSub: 'A veil, a cape or gloves to finish your gown: colours and sizes are chosen on each page.', lookAll: 'See all accessories', choose: 'Choose',
    step2: 'Bust, waist, hips, hollow to floor and heel height, right on each gown’s page.',
    quizWa: 'A question about these gowns? Ask us on WhatsApp',
    quizMsg: s => `Hello, the Serment Couture quiz suggests: ${s}. Could you advise me?`,
    dateMsg: (p, d) => `Hello, my wedding is on ${d} and I am thinking of ordering: ${p}. Is the timing right?`,
    veils: 'Veils', capes: 'Capes', gloves: 'Gloves', vowery: 'Vowery', acc: 'Accessories',
    veilLen: 'Veil length', len: 'Length', lenHelp: 'Which length to choose?', shadeHelp: 'White, ivory or off-white?'
  };
  const EU = { '2': 32, '4': 34, '6': 36, '8': 38, '10': 40, '12': 42, '14': 44, '16': 46, '14w': 44, '16w': 46, '18w': 48, '20w': 50, '22w': 52, '24w': 54, '26w': 56 };
  const LET = { '2': 'S', '4': 'S', '6': 'M', '8': 'M', '10': 'L', '12': 'L', '14': 'XL', '16': 'XL' };
  const esc = s => String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const waNum = () => { const a = document.querySelector('a[href*="wa.me/"]'); const m = a && a.getAttribute('href').match(/wa\.me\/(\d+)/); return m ? m[1] : ''; };
  const waLink = txt => { const n = waNum(); return n ? `https://wa.me/${n}?text=${encodeURIComponent(txt)}` : ''; };
  const store = { get(){ try { return localStorage.getItem('serment_wedding') || ''; } catch (e) { return ''; } }, set(v){ try { localStorage.setItem('serment_wedding', v); } catch (e) {} } };
  const fmt = d => new Intl.DateTimeFormat(LOC, { day: 'numeric', month: 'long' }).format(d);
  const fmtFull = d => new Intl.DateTimeFormat(LOC, { day: 'numeric', month: 'long', year: 'numeric' }).format(d);
  const day0 = d => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
  const saveToCart = iso => fetch(ROOT + 'cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: { 'Wedding date': iso } }) }).catch(() => {});

  /* ── 1. Wedding-date check (product page, under the delivery line; cart summary) ── */
  function dateWidget(j1, j2, productName, compact){
    const box = document.createElement('div'); box.className = 'wd' + (compact ? ' wd-compact' : '');
    const today = day0(new Date());
    const min = new Date(today); min.setDate(min.getDate() + 1);
    box.innerHTML = `${compact ? '' : `<button type="button" class="wd-ask" aria-expanded="false">${T.ask}</button>`}
      <div class="wd-body"${compact ? '' : ' hidden'}>
        <label class="wd-l"><span>${T.date}</span><input type="date" min="${min.toISOString().slice(0, 10)}" data-wd-in></label>
        <div class="wd-res" aria-live="polite" hidden></div>
      </div>`;
    const inp = box.querySelector('[data-wd-in]'), res = box.querySelector('.wd-res'), body = box.querySelector('.wd-body');
    const ask = box.querySelector('.wd-ask');
    if (ask) ask.addEventListener('click', () => { body.hidden = !body.hidden; ask.setAttribute('aria-expanded', String(!body.hidden)); if (!body.hidden) setTimeout(() => inp.focus({ preventScroll: true }), 60); });
    const run = (save) => {
      if (!inp.value) { res.hidden = true; return; }
      const w = day0(new Date(inp.value + 'T12:00:00'));
      const a = new Date(today); a.setDate(a.getDate() + j1);
      const b = new Date(today); b.setDate(b.getDate() + j2);
      const gap = Math.round((w - b) / 864e5);
      let html, cls;
      if (w <= today) { html = T.past; cls = 'is-late'; }
      else if (gap > 28) { html = T.ok(fmt(a), fmt(b), Math.floor(gap / 7)); cls = 'is-ok'; }
      else if (gap > 0) { html = T.tight(fmt(a), fmt(b), gap); cls = 'is-tight'; }
      else { html = T.late; cls = 'is-late'; }
      let extra = '';
      if (cls !== 'is-ok' && w > today) { const l = waLink(T.dateMsg(productName || document.title, fmtFull(w))); if (l) extra = `<a class="wd-wa" href="${l}" target="_blank" rel="noopener">${T.wa}</a>`; }
      res.className = 'wd-res ' + cls;
      res.innerHTML = `<p>${html}</p>${extra}<p class="wd-note">${T.note}${save ? ' ' + T.saved : ''}</p>`;
      res.hidden = false;
    };
    inp.addEventListener('change', () => { store.set(inp.value); if (inp.value) saveToCart(inp.value); run(true); });
    const prev = store.get();
    if (prev && prev > today.toISOString().slice(0, 10)) { inp.value = prev; run(false); if (!compact && ask) { /* keep closed until asked */ } }
    return box;
  }
  function productPage(){
    const del = document.querySelector('.pdp-delivery[data-delivery]');
    if (!del || del.dataset.wdReady) return;
    del.dataset.wdReady = '1';
    const j1 = parseInt(del.dataset.j1 || '12', 10), j2 = parseInt(del.dataset.j2 || '21', 10);
    const h1 = document.querySelector('h1');
    del.insertAdjacentElement('afterend', dateWidget(j1, j2, h1 ? h1.textContent.trim() : '', false));
  }

  /* ── 2. Breadcrumb: accessories and Vowery are not "Gowns" ── */
  function breadcrumb(){
    const bc = document.querySelector('.pdp-info .breadcrumb, .breadcrumb');
    const meta = window.ShopifyAnalytics && ShopifyAnalytics.meta && ShopifyAnalytics.meta.product;
    if (!bc || !meta || !document.body.classList.contains('template-product')) return;
    const type = String(meta.type || '').toLowerCase();
    if (!type || /wedding dress|robe de mari/.test(type)) return;
    let label = T.acc, url = 'collections/bridal-accessories';
    if (/veil|voile/.test(type)) { label = T.veils; url = 'collections/wedding-veils'; }
    else if (/cape|bol[eé]ro|shawl|ch[aâ]le/.test(type)) { label = T.capes; url = 'collections/capes-boleros'; }
    else if (/glove|gant/.test(type)) { label = T.gloves; url = 'collections/bridal-gloves'; }
    else if (/vowery|invitation|template|canva|digital/.test(type)) { label = T.vowery; url = 'collections/vowery'; }
    const links = bc.querySelectorAll('a');
    const mid = links.length > 1 ? links[1] : null;
    if (mid && /collections\/(all|wedding-dress)/.test(mid.getAttribute('href') || '')) { mid.textContent = label; mid.setAttribute('href', ROOT + url); }
  }

  /* ── 3. Cart page ── */
  async function cartPage(){
    const form = document.getElementById('cartForm');
    if (!form || form.dataset.fnReady) return;
    form.dataset.fnReady = '1';
    let cart; try { cart = await fetch(ROOT + 'cart.js').then(r => r.json()); } catch (e) { return; }
    if (!cart || !cart.items || !cart.items.length) return;
    /* sizes: "6" -> "Size US 6 · EU 36 · M" */
    form.querySelectorAll('.c-line .variant').forEach(v => {
      const parts = v.textContent.split('/').map(s => s.trim());
      const out = parts.map(p => { const k = p.toLowerCase().replace(/^us\s*/, '').replace(/\s+/g, ''); return EU[k] ? `${T.size} US ${p.replace(/\s+W$/i, 'W')} · EU ${EU[k]}${LET[k] ? ' · ' + LET[k] : ''}` : p; });
      if (out.join(' / ') !== parts.join(' / ')) v.textContent = out.join(' / ');
    });
    const gown = cart.items.some(i => /wedding dress|robe de mari/i.test(i.product_type || ''));
    const hasAcc = cart.items.some(i => /veil|voile|cape|bol[eé]ro|glove|gant|shawl|ch[aâ]le|accessor/i.test(i.product_type || ''));
    /* step text says "on this page": on the cart it means each gown's page */
    document.querySelectorAll('.m-etapes p').forEach(p => { if (/right on this page|directement sur cette page/i.test(p.textContent)) p.textContent = T.step2; });
    /* wedding date in the summary (delivery window read from any gown in the cart: its product page block) */
    const sum=null;if(sum&&gown){
      let j1 = 12, j2 = 21;
      try {
        const g = cart.items.find(i => /wedding dress|robe de mari/i.test(i.product_type || ''));
        const html = await fetch(ROOT + 'products/' + g.handle).then(r => r.text());
        const m1 = html.match(/data-delivery[^>]*data-j1="(\d+)"[^>]*data-j2="(\d+)"/);
        if (m1) { j1 = parseInt(m1[1], 10); j2 = parseInt(m1[2], 10); }
      } catch (e) {}
      const names = cart.items.map(i => i.product_title).join(', ');
      const w = dateWidget(j1, j2, names, true);
      const btn = sum.querySelector('[name="checkout"]');
      (btn ? btn.parentElement : sum.lastElementChild).insertAdjacentElement('beforebegin', w);
      const attr = cart.attributes && cart.attributes['Wedding date'];
      if (attr && !store.get()) { const i = w.querySelector('[data-wd-in]'); i.value = attr; i.dispatchEvent(new Event('change')); }
    }
    /* complete the look: accessories rather than more gowns */
    if (gown && !hasAcc) {
      const robes = document.querySelector('.couture-robes');
      if (robes) { const sec = robes.closest('.shopify-section') || robes; sec.hidden = true; }
      try {
        const data = await fetch(ROOT + 'collections/bridal-accessories/products.json?limit=24').then(r => r.json());
        const ps = (data.products || []).filter(p => p.images && p.images.length && p.variants.some(v => v.available));
        for (let i = ps.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [ps[i], ps[j]] = [ps[j], ps[i]]; }
        const pick = []; const seenType = {};
        ps.forEach(p => { const t = (p.product_type || '').toLowerCase(); if (pick.length < 3 && !seenType[t]) { seenType[t] = 1; pick.push(p); } });
        ps.forEach(p => { if (pick.length < 3 && pick.indexOf(p) === -1) pick.push(p); });
        if (pick.length) {
          const sample = (form.querySelector('.c-line .unit') || form).textContent || '';
          const comma = /\d,\d{2}(?!\d)/.test(sample);
          const money = c => { const t = new Intl.NumberFormat('en-GB', { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR' }).format(c); return comma ? t.replace(/,/g, ' ').replace(/\.(\d{2})$/, ',$1').replace(/ /g, '.') : t; };
          const img = p => { const s = p.images[0].src; return s.replace(/(\.[a-z]+)(\?.*)?$/i, '_400x$1'); };
          const box = document.createElement('aside'); box.className = 'cl-look';
          box.innerHTML = `<p class="cl-k">${T.look}</p><p class="cl-sub">${T.lookSub}</p><div class="cl-cards">${pick.map(p => `<a class="cl-card" href="${ROOT}products/${p.handle}"><img src="${img(p)}" alt="${esc(p.title)}" loading="lazy" width="400" height="400"><span class="cl-t">${esc(p.title)}</span><span class="cl-p">${money(parseFloat(p.variants.find(v => v.available).price))}</span><span class="cl-go">${T.choose} →</span></a>`).join('')}</div><a class="cl-all" href="${ROOT}collections/bridal-accessories">${T.lookAll}</a>`;
          const vw = form.querySelector('.vw-match');
          const lines = form.querySelector('.c-line') ? form.querySelector('.c-line').parentElement : null;
          const lk = form.querySelector('.cart-look'); if (lk) lk.insertAdjacentElement('afterbegin', box); else if (vw) vw.insertAdjacentElement('beforebegin', box); else if (lines) lines.appendChild(box);
        }
      } catch (e) {}
    }
  }

  /* ── 4. Drawer: an accessory with several colours opens its page instead of adding the first one ── */
  function drawerGuard(){
    const zone = document.getElementById('drawerGift');
    if (!zone || zone.dataset.fnObs) return;
    zone.dataset.fnObs = '1';
    const cache = {};
    const fix = () => zone.querySelectorAll('.d-gift-card').forEach(async card => {
      const btn = card.querySelector('button.d-gift-add'); const a = card.querySelector('a[href*="/products/"]');
      if (!btn || !a || card.dataset.fnChecked) return;
      card.dataset.fnChecked = '1';
      const handle = a.getAttribute('href').split('/products/')[1].split(/[?#]/)[0];
      try {
        cache[handle] = cache[handle] || fetch(ROOT + 'products/' + handle + '.js').then(r => r.json());
        const p = await cache[handle];
        if (p.variants && p.variants.filter(v => v.available).length > 1) {
          const link = document.createElement('a'); link.className = 'd-gift-add d-gift-go'; link.href = a.getAttribute('href'); link.textContent = T.choose;
          btn.replaceWith(link);
        }
      } catch (e) {}
    });
    new MutationObserver(fix).observe(zone, { childList: true, subtree: true });
    fix();
  }

  /* ── 5. Quiz results: a human hand-off ── */
  function quiz(){
    document.querySelectorAll('.quiz-result[data-result]').forEach(r => {
      if (r.querySelector('.qz-wa')) return;
      const h = r.querySelector('h2'); const l = waLink(T.quizMsg(h ? h.textContent.trim() : ''));
      if (!l) return;
      const a = document.createElement('a'); a.className = 'qz-wa'; a.href = l; a.target = '_blank'; a.rel = 'noopener'; a.textContent = T.quizWa;
      r.appendChild(a);
    });
  }


  /* ── 6. Option helpers: lengths in order ("300 cm" before "350 cm"), clear labels, a guide one click away ── */
  function options(){
    const meta = window.ShopifyAnalytics && ShopifyAnalytics.meta && ShopifyAnalytics.meta.product;
    const type = String((meta && meta.type) || '').toLowerCase();
    if (!type || /template|service/.test(type)) return;
    document.querySelectorAll('[data-opt-group]').forEach(g => {
      if (g.dataset.fnOpt) return;
      const lbl = g.previousElementSibling && g.previousElementSibling.classList.contains('opt-label') ? g.previousElementSibling : null;
      if (!lbl) return;
      const name = lbl.textContent.toLowerCase();
      if (/length|longueur/.test(name)) {
        g.dataset.fnOpt = '1';
        const pills = Array.from(g.querySelectorAll('.v-pill'));
        const num = p => { const i = p.querySelector('input'); const m = i && i.value.match(/(\d+(?:[.,]\d+)?)/); return m ? parseFloat(m[1].replace(',', '.')) : 1e9; };
        pills.slice().sort((a, b) => num(a) - num(b)).forEach(p => g.appendChild(p));
        pills.forEach(p => Array.from(p.childNodes).forEach(n => { if (n.nodeType === 3 && /\d\s*cm/i.test(n.textContent)) n.textContent = n.textContent.replace(/(\d)\s*cm/i, '$1 cm'); }));
        lbl.textContent = /veil|voile/.test(type) ? T.veilLen : T.len;
        if (/veil|voile/.test(type)) lbl.insertAdjacentHTML('beforeend', ` <a class="opt-help" href="${ROOT}blogs/journal/wedding-veil-length-guide" target="_blank" rel="noopener">${T.lenHelp}</a>`);
      } else if (/colou?r|couleur/.test(name) && /wedding dress|robe|veil|voile|cape|bol[eé]ro|shawl|ch[aâ]le|glove|gant/.test(type)) {
        g.dataset.fnOpt = '1';
        const vals = Array.from(g.querySelectorAll('input')).map(i => i.value.toLowerCase()).join(' ');
        if (/ivory|ivoire|off.?white|blanc cass|champagne/.test(vals)) lbl.insertAdjacentHTML('beforeend', ` <a class="opt-help" href="${ROOT}blogs/journal/ivory-vs-white-vs-champagne-wedding-dress" target="_blank" rel="noopener">${T.shadeHelp}</a>`);
      }
    });
  }

  /* ── 7. Order with confidence: the site's own commitments, gathered under the add-to-cart button ── */
  function confidence(){
    const note = document.querySelector('.pdp-info .buy-note');
    const meta = window.ShopifyAnalytics && ShopifyAnalytics.meta && ShopifyAnalytics.meta.product;
    const type = String((meta && meta.type) || '').toLowerCase();
    if (!note || note.dataset.fnConf || !type || /template|service|website|mod[eè]le|site/.test(type)) return;
    note.dataset.fnConf = '1';
    const gown = /wedding dress|robe de mari/.test(type);
    const ico = {
      cut: '<circle cx="6" cy="7" r="2.5"/><circle cx="6" cy="17" r="2.5"/><path d="M8.2 8.3L20 18M8.2 15.7L20 6"/>',
      box: '<path d="M3 8l9-4 9 4v8l-9 4-9-4z"/><path d="M3 8l9 4 9-4M12 12v8"/>',
      tag: '<path d="M4 12V4h8l8 8-8 8z"/><circle cx="8.5" cy="8.5" r="1.3"/>',
      back: '<path d="M9 7H4v5"/><path d="M4 12a8 8 0 1 0 2.3-5.7L4 8.5"/>',
      shield: '<path d="M12 3l8 3v6c0 4.4-3.4 8-8 9-4.6-1-8-4.6-8-9V6z"/><path d="M8.5 12l2.5 2.5 4.5-5"/>',
      lock: '<rect x="5" y="10" width="14" height="10" rx="1.5"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/>',
      chat: '<path d="M4 5h16v11H9l-5 4z"/>'
    };
    const wa = waNum();
    const F = FR ? {
      h: 'Commander en toute confiance',
      items: [
        gown ? ['cut', 'Faite pour vous', 'Confectionnée à votre taille ou à vos mesures, puis inspectée couture par couture avant l’envoi.'] : null,
        ['box', 'Livraison suivie offerte', '7 à 14 jours ouvrés, avec un lien de suivi par e-mail dès que le colis part.'],
        ['tag', 'Aucun frais à la livraison', 'Le prix payé est le prix final : hors Union européenne, nous réglons nous-mêmes TVA et droits de douane.'],
        gown ? ['back', '14 jours pour changer d’avis', 'En taille standard, renvoyez la robe non portée dans son emballage d’origine. Une robe coupée à vos mesures est personnalisée et ne se retourne pas, sauf défaut.']
             : ['back', '14 jours pour changer d’avis', 'Renvoyez l’article non porté, dans son emballage d’origine. Les frais de retour sont à votre charge, sauf défaut.'],
        ['shield', 'Nos erreurs, à nos frais', 'Un défaut, un colis abîmé ou une erreur de notre part : réparé, remplacé ou remboursé. Garantie légale de conformité de 2 ans.'],
        ['lock', 'Paiement sécurisé', 'Visa, Mastercard, Maestro, paiement chiffré.'],
        ['chat', 'De vraies personnes', 'Réponse écrite sous 24 heures, 7 jours sur 7' + (wa ? ', ou tout de suite sur WhatsApp.' : '.')]
      ],
      more: 'Livraison et retours en détail', waL: 'Écrire sur WhatsApp'
    } : {
      h: 'Order with confidence',
      items: [
        gown ? ['cut', 'Made for you', 'Made in your size or to your measurements, then inspected seam by seam before it leaves.'] : null,
        ['box', 'Complimentary tracked shipping', '7 to 14 business days, with a tracking link by email as soon as your parcel leaves.'],
        ['tag', 'Nothing to pay on delivery', 'The price at checkout is final: outside the European Union, we settle import VAT and duties ourselves.'],
        gown ? ['back', '14 days to change your mind', 'In a standard size, send the gown back unworn in its original packaging. A gown cut to your measurements is personalised and cannot be returned, except for a defect.']
             : ['back', '14 days to change your mind', 'Send the piece back unworn, in its original packaging. Return shipping is yours to pay, unless it is faulty.'],
        ['shield', 'Our mistakes, at our cost', 'A defect, a damaged parcel or an error on our side is repaired, replaced or refunded. Two-year legal guarantee of conformity.'],
        ['lock', 'Secure payment', 'Visa, Mastercard, Maestro, encrypted checkout.'],
        ['chat', 'Real people', 'A written reply within 24 hours, 7 days a week' + (wa ? ', or straight away on WhatsApp.' : '.')]
      ],
      more: 'Shipping and returns in full', waL: 'Write on WhatsApp'
    };
    const box = document.createElement('div'); box.className = 'cf';
    box.innerHTML = `<p class="cf-h">${F.h}</p><ul class="cf-list">${F.items.filter(Boolean).map(([i, t, d]) => `<li><svg viewBox="0 0 24 24" aria-hidden="true">${ico[i]}</svg><div><b>${t}</b><span>${d}</span></div></li>`).join('')}</ul><div class="cf-foot"><a href="${ROOT}pages/shipping-returns">${F.more} →</a>${wa ? `<a href="https://wa.me/${wa}" target="_blank" rel="noopener">${F.waL} →</a>` : ''}</div>`;
    note.insertAdjacentElement('afterend', box);
    const old = document.querySelector('.pdp-info .trust-badges'); if (old) old.hidden = true;
  }

  const run = () => { [confidence, productPage, breadcrumb, options, cartPage, drawerGuard, quiz].forEach(f => { try { const p = f(); if (p && p.catch) p.catch(() => {}); } catch (e) { if (window.console) console.warn('funnels', e); } }); };
  if (document.readyState === 'complete') setTimeout(run, 0); else document.addEventListener('DOMContentLoaded', () => setTimeout(run, 0));
  window.addEventListener('load', run);
  try { new MutationObserver(() => { if (document.getElementById('drawerGift')) drawerGuard(); }).observe(document.body, { childList: true }); } catch (e) {}
})();

/* ═══ Serment lightbox: product photos open full screen, with arrows, keyboard and swipe ═══ */
(function () {
  var ICON = {
    open: '<path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/>',
    close: '<path d="M6 6l12 12M18 6L6 18"/>',
    prev: '<path d="M15 5l-7 7 7 7"/>',
    next: '<path d="M9 5l7 7-7 7"/>'
  };
  var svg = function (k) { return '<svg viewBox="0 0 24 24" aria-hidden="true">' + ICON[k] + '</svg>'; };

  function init() {
    var main = document.querySelector('.pdp-gallery .g-main');
    if (!main || main.getAttribute('data-lbx')) return;
    var slides = [].slice.call(main.querySelectorAll('.g-slide'));
    var imgs = slides.map(function (s) { return s.querySelector('img.g-photo'); });
    if (!imgs.length || imgs.some(function (i) { return !i; })) return;
    main.setAttribute('data-lbx', '1');

    var fr = /^fr/i.test(document.documentElement.lang || '');
    var T = fr
      ? { open: 'Agrandir la photo', prev: 'Photo précédente', next: 'Photo suivante', close: 'Fermer', label: 'Photos du produit' }
      : { open: 'View larger', prev: 'Previous photo', next: 'Next photo', close: 'Close', label: 'Product photos' };
    var n = imgs.length, idx = 0, lb = null, pic = null, cnt = null, lastFocus = null, token = 0;

    function url(i, w) {
      var im = imgs[i];
      var u = im.getAttribute('data-g-src') || im.getAttribute('src') || '';
      if (/^data:/.test(u)) u = '';
      if (!u) return '';
      return /[?&]width=\d+/.test(u) ? u.replace(/([?&])width=\d+/, '$1width=' + w) : u + (u.indexOf('?') < 0 ? '?' : '&') + 'width=' + w;
    }
    function bigW() { return (window.innerWidth * (window.devicePixelRatio || 1)) > 1500 ? 2000 : 1400; }
    function preload(i) { var u = url((i + n) % n, bigW()); if (u) { var im = new Image(); im.src = u; } }

    function show(i) {
      idx = (i + n) % n;
      var my = ++token, hi = url(idx, bigW()), lo = url(idx, 1200);
      pic.alt = imgs[idx].alt || '';
      if (lo) pic.src = lo;
      if (hi && hi !== lo) {
        var h = new Image();
        h.onload = function () { if (my === token) pic.src = hi; };
        h.src = hi;
      }
      if (cnt) cnt.textContent = (idx + 1) + ' / ' + n;
      if (n > 1) { preload(idx + 1); preload(idx - 1); }
    }

    function build() {
      lb = document.createElement('div');
      lb.className = 'lbx' + (n < 2 ? ' lbx-single' : '');
      lb.setAttribute('role', 'dialog');
      lb.setAttribute('aria-modal', 'true');
      lb.setAttribute('aria-label', T.label);
      lb.innerHTML =
        '<div class="lbx-stage"><img class="lbx-img" alt="" draggable="false"></div>' +
        '<button type="button" class="lbx-close" aria-label="' + T.close + '">' + svg('close') + '</button>' +
        (n > 1
          ? '<button type="button" class="lbx-nav lbx-prev" aria-label="' + T.prev + '">' + svg('prev') + '</button>' +
            '<button type="button" class="lbx-nav lbx-next" aria-label="' + T.next + '">' + svg('next') + '</button>' +
            '<div class="lbx-count" aria-live="polite"></div>'
          : '');
      document.body.appendChild(lb);
      pic = lb.querySelector('.lbx-img');
      cnt = lb.querySelector('.lbx-count');
      lb.addEventListener('click', function (e) {
        if (e.target.closest('.lbx-close')) return close();
        if (e.target.closest('.lbx-prev')) return show(idx - 1);
        if (e.target.closest('.lbx-next')) return show(idx + 1);
        if (e.target === lb || e.target.classList.contains('lbx-stage')) close();
      });
      var sx = null, sy = null;
      lb.addEventListener('touchstart', function (e) { if (e.touches.length === 1) { sx = e.touches[0].clientX; sy = e.touches[0].clientY; } }, { passive: true });
      lb.addEventListener('touchend', function (e) {
        if (sx === null) return;
        var dx = e.changedTouches[0].clientX - sx, dy = e.changedTouches[0].clientY - sy;
        sx = sy = null;
        if (n > 1 && Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) show(dx < 0 ? idx + 1 : idx - 1);
        else if (dy > 90 && Math.abs(dy) > Math.abs(dx)) close();
      }, { passive: true });
    }

    function onKey(e) {
      if (!lb || !lb.classList.contains('on')) return;
      if (e.key === 'Escape') { e.preventDefault(); close(); }
      else if (e.key === 'ArrowLeft' && n > 1) { e.preventDefault(); show(idx - 1); }
      else if (e.key === 'ArrowRight' && n > 1) { e.preventDefault(); show(idx + 1); }
      else if (e.key === 'Tab') {
        var f = [].slice.call(lb.querySelectorAll('button'));
        var k = f.indexOf(document.activeElement);
        e.preventDefault();
        f[(k + (e.shiftKey ? -1 : 1) + f.length) % f.length].focus();
      }
    }

    function current() { var k = slides.findIndex(function (s) { return s.classList.contains('on'); }); return k < 0 ? 0 : k; }

    function open(i) {
      if (!lb) build();
      lastFocus = document.activeElement;
      show(i);
      lb.classList.add('on');
      document.documentElement.classList.add('lbx-lock');
      document.addEventListener('keydown', onKey, true);
      requestAnimationFrame(function () { requestAnimationFrame(function () { lb.classList.add('open'); }); });
      var c = lb.querySelector('.lbx-close'); if (c) c.focus({ preventScroll: true });
    }

    function close() {
      if (!lb) return;
      lb.classList.remove('open');
      document.documentElement.classList.remove('lbx-lock');
      document.removeEventListener('keydown', onKey, true);
      setTimeout(function () { if (!lb.classList.contains('open')) lb.classList.remove('on'); }, 260);
      /* the page gallery follows the last photo seen */
      var cur = current(), fwd = (idx - cur + n) % n;
      var btn = main.querySelector(fwd <= n / 2 ? '[data-g-next]' : '[data-g-prev]');
      var steps = fwd <= n / 2 ? fwd : n - fwd;
      if (btn) for (var s = 0; s < steps; s++) btn.click();
      if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    }

    var ob = document.createElement('button');
    ob.type = 'button';
    ob.className = 'lbx-open';
    ob.setAttribute('aria-label', T.open);
    ob.innerHTML = svg('open');
    main.appendChild(ob);
    ob.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); open(current()); });

    main.addEventListener('click', function (e) {
      if (e.target.closest('button, a, .g-count')) return;
      if (!e.target.closest('.g-slide')) return;
      open(current());
    });
    main.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && e.target === main) { e.preventDefault(); open(current()); }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  window.addEventListener('load', init);
})();


/* ═══ Serment: an option with a single value is information, not a choice ═══
   Display only: the radio stays checked in the DOM, variants are untouched. */
(function () {
  var FR = /^fr/i.test(document.documentElement.lang || '');
  var T = FR ? {
    train: 'Traîne', len: 'Longueur', veilLen: 'Longueur du voile', size: 'Taille', colour: 'Coloris',
    custom: 'Sur mesure', one: 'Taille unique',
    note: 'Cette pièce est réalisée à vos mesures. Renseignez-les ci-dessous avant de l’ajouter au panier.',
    enter: 'Saisir mes mensurations'
  } : {
    train: 'Train', len: 'Length', veilLen: 'Veil length', size: 'Size', colour: 'Colour',
    custom: 'Made to measure', one: 'One size',
    note: 'This piece is made to your measurements. Enter them below before adding it to your bag.',
    enter: 'Enter my measurements'
  };
  var esc = function (s) { return String(s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); };
  var cleanColour = function (v) {
    v = String(v).replace(/^\s*\d+\s*/, '').replace(/[-\s]+\d+\s*$/, '').trim();
    v = v ? v.charAt(0).toUpperCase() + v.slice(1) : v;
    var fr = { 'white': 'Blanc', 'ivory': 'Ivoire', 'black': 'Noir', 'off white': 'Blanc cassé', 'navy': 'Marine', 'champagne': 'Champagne' };
    return FR && fr[v.toLowerCase()] ? fr[v.toLowerCase()] : v;
  };

  function snap() {
    document.querySelectorAll('.opt-label').forEach(function (l) { if (!l.dataset.rawName) l.dataset.rawName = l.textContent.trim(); });
  }
  if (document.readyState !== 'loading') snap(); else document.addEventListener('DOMContentLoaded', snap);

  function init() {
    snap();
    var meta = window.ShopifyAnalytics && ShopifyAnalytics.meta && ShopifyAnalytics.meta.product;
    var type = String((meta && meta.type) || '').toLowerCase();
    if (!type || /template|service/.test(type)) return;
    document.querySelectorAll('[data-opt-group]').forEach(function (g) {
      var pills = g.querySelectorAll('.v-pill');
      if (pills.length !== 1) return;
      var lbl = g.previousElementSibling && g.previousElementSibling.classList.contains('opt-label') ? g.previousElementSibling : null;
      var inp = pills[0].querySelector('input');
      if (!lbl || !inp) return;
      if (lbl.querySelector('b.opt-one-v')) return;
      var label, val, custom = false;
      if (g.dataset.oneOpt) { label = g.dataset.oneLabel; val = g.dataset.oneVal; }
      else {
        var name = (lbl.dataset.rawName || lbl.textContent).toLowerCase();
        val = inp.value;
        var lv = val.toLowerCase();
        label = lbl.textContent.trim();
        if (/train|tra[iî]ne/.test(name)) { label = T.train; val = val.replace(/(\d)\s*cm/i, '$1 cm'); }
        else if (/length|longueur/.test(name)) { label = /veil|voile/.test(type) ? T.veilLen : T.len; val = val.replace(/(\d)\s*cm/i, '$1 cm'); }
        else if (/size|taille/.test(name)) {
          label = T.size;
          if (/custom|mesure|personnalis/.test(lv)) { val = T.custom; custom = true; }
          else if (/one\s*size|taille unique|free/.test(lv)) val = T.one;
        }
        else if (/colou?r|couleur|coloris/.test(name)) { label = T.colour; val = cleanColour(val); }
        g.dataset.oneOpt = '1'; g.dataset.oneLabel = label; g.dataset.oneVal = val;
      }
      var help = Array.prototype.slice.call(lbl.querySelectorAll('.opt-help'));
      lbl.classList.add('opt-one');
      lbl.classList.remove('sz-label');
      lbl.innerHTML = '<span>' + esc(label) + '</span><b class="opt-one-v">' + esc(val) + '</b>';
      help.forEach(function (a) { lbl.appendChild(a); });
      g.hidden = true;
      g.classList.add('opt-one-g');
      if (custom) {
        var cs = document.querySelector('[data-custom-size]');
        if (cs && !cs.classList.contains('cs-solo')) {
          cs.classList.add('cs-solo');
          var t = cs.querySelector('[data-cs-toggle]');
          if (t) {
            Array.prototype.slice.call(t.childNodes).forEach(function (n) { if (n.nodeType === 3 && n.textContent.trim()) n.textContent = ' ' + T.enter + ' '; });
          }
          var note = document.createElement('p');
          note.className = 'opt-one-note';
          note.textContent = T.note;
          lbl.insertAdjacentElement('afterend', note);
        }
      }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { setTimeout(init, 0); });
  else setTimeout(init, 0);
  window.addEventListener('load', function () { setTimeout(init, 50); setTimeout(init, 800); });
})();
